import {Renderer} from 'amis';
import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import {unstable_batchedUpdates} from 'react-dom';
import {Layout, Spin, message} from 'antd';
import * as VTable from '@visactor/vtable';
import {createGroup, createText, createImage, Tag, CheckBox, Radio} from '@visactor/vtable/es/vrender';
import {
  DateInputEditor,
  InputEditor,
  ListEditor,
  TextAreaEditor
} from '@visactor/vtable-editors';
import {
  ImageEditor,
  ImageEditorConfig
} from './components/CustomEditor/ImageEditor';
import {
  FileEditor,
  FileEditorConfig
} from './components/CustomEditor/FileEditor';
import * as XLSX from 'xlsx';
import {
  downloadExcel,
  exportVTableToExcel,
  downloadCsv,
  exportVTableToCsv
} from '@visactor/vtable-export';
import 'antd/dist/antd.css';

import {useDirectusAPI} from './hooks/useDirectusAPI';
import {SiderPanel} from './components/SiderPanel';
import {DashboardHeaderToolbar} from './components/HeaderToolbar/DashboardHeaderToolbar';
import {SheetHeaderToolbar} from './components/HeaderToolbar/SheetHeaderToolbar';
import {FieldEditorCard} from './components/FieldEditorCard';
import {ImportConfigModal} from './components/ImportConfigModal';
// import {DashboardView} from './components/DashboardView';
import {DashboardView} from '../DashboardPlugin/index';
import {ViewTabs, ViewConfig} from './components/HeaderToolbar/ViewTabs';
import {
  GridPluginProps,
  SheetInfo,
  FieldInfo,
  CardPosition,
  ColumnTypeMapping,
  DashboardInfo,
  ChartConfig,
  FilterRule
} from './types';
import {svgIconMap, cellTypeMap} from './constants';
import './style/index.css';

const {Content} = Layout;
const {
  CLICK_CELL,
  MOUSEDOWN_TABLE,
  CONTEXTMENU_CELL,
  COPY_DATA,
  DROPDOWN_MENU_CLICK,
  CHANGE_CELL_VALUE,
  CHANGE_HEADER_POSITION,
  DBLCLICK_CELL,
  MOUSEUP_CELL,
  RESIZE_COLUMN_END
} = VTable.ListTable.EVENT_TYPE;

// Register VTable Editors & Icons
const inputEditor = new InputEditor();
const numberEditor = new InputEditor();
const textAreaEditor = new TextAreaEditor();
const dateInputEditor = new DateInputEditor();
// 创建自定义编辑器实例
// const imageEditor = new ImageEditor({
//   maxSize: 10, // 10MB
//   multiple: false,
//   onUpload: async (file: File) => {
//     // 这里可以接入实际的上传逻辑
//     // 目前返回base64，实际项目中应该上传到服务器并返回URL
//     return new Promise((resolve, reject) => {
//       const reader = new FileReader();
//       reader.onload = () => resolve(reader.result as string);
//       reader.onerror = reject;
//       reader.readAsDataURL(file);
//     });
//   }
// });

// const fileEditor = new FileEditor({
//   maxSize: 50, // 50MB
//   multiple: true,
//   onUpload: async (file: File) => {
//     // 这里可以接入实际的上传逻辑
//     return {
//       url: URL.createObjectURL(file), // 实际项目中应该是服务器返回的URL
//       name: file.name,
//       size: file.size
//     };
//   }
// });
VTable.register.editor('text-editor', inputEditor);
VTable.register.editor('textArea-editor', textAreaEditor);
VTable.register.editor('number-editor', numberEditor);
VTable.register.editor('date-editor', dateInputEditor);
// VTable.register.editor('image-editor', imageEditor);
// VTable.register.editor('file-editor', fileEditor);

VTable.register.icon('filter', {
  name: 'filter',
  type: 'svg',
  width: 20,
  height: 20,
  marginRight: 6,
  positionType: VTable.TYPES.IconPosition.right,
  svg: svgIconMap['filter']
});
VTable.register.icon('filtered', {
  name: 'filtered',
  type: 'svg',
  width: 20,
  height: 20,
  marginRight: 6,
  positionType: VTable.TYPES.IconPosition.right,
  svg: svgIconMap['filtered']
});

const AgimxGrid: React.FC<GridPluginProps> = props => {
  const {api, userId, userInfo} = useDirectusAPI(props);

  // State Management
  const [loading, setLoading] = useState(true);
  const [allSheetList, setAllSheetList] = useState<SheetInfo[]>([]);
  const [filterSheetList, setFilterSheetList] = useState<SheetInfo[]>([]);
  const [currentSheet, setCurrentSheet] = useState<SheetInfo | null>(null);
  const [currentSheetPermissions, setCurrentSheetPermissions] = useState<
    string[]
  >([]);
  const [selectedCollection, setSelectedCollection] = useState<string>('');
  const [focusCollection, setFocusCollection] = useState('');
  const [focusSheet, setFocusSheet] = useState<SheetInfo | null>(null);
  const [focusSheetPermissions, setFocusSheetPermissions] = useState<string[]>(
    []
  );
  const [focusSheetPermissionIds, setFocusSheetPermissionIds] = useState<
    number[]
  >([]);
  const [isSheetCardVisible, setSheetCardVisible] = useState(false);
  const [isCardVisible, setCardVisible] = useState(false);
  const [isRenameSheet, setRenameSheet] = useState(false);
  const [operateType, setOperateType] = useState<'add' | 'edit'>('add');
  const [postFieldName, setPostFieldName] = useState<string>('');
  const [editField, setEditField] = useState('');
  const [cardPosition, setCardPosition] = useState<CardPosition>({
    top: 0,
    left: 0
  });
  const [selectedFieldType, setSelectedFieldType] = useState('text');
  const [currentRow, setCurrentRow] = useState(0);
  const [currentCol, setCurrentCol] = useState(0);
  const [direction, setDirection] = useState('right');
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  const [draggingField, setDraggingField] = useState<string | null>(null);
  const [fieldList, setFieldList] = useState<FieldInfo[]>([]);
  const [isImportModalVisible, setImportModalVisible] = useState(false);
  const [importHeaders, setImportHeaders] = useState<string[]>([]);
  const [importData, setImportData] = useState<any[][]>([]);
  const [columnTypeMappings, setColumnTypeMappings] =
    useState<ColumnTypeMapping>({});
  const [importFileName, setImportFileName] = useState('');
  const [tableReady, setTableReady] = useState(false);
  // --- 仪表盘 State ---
  const [allDashboardList, setAllDashboardList] = useState<DashboardInfo[]>([]);
  const [filterDashboardList, setFilterDashboardList] = useState<
    DashboardInfo[]
  >([]);
  const [selectedDashboard, setSelectedDashboard] = useState<number | null>(
    null
  );
  const [focusDashboard, setFocusDashboard] = useState<number | null>(null);
  const [focusDashboardInfo, setFocusDashboardInfo] =
    useState<DashboardInfo | null>(null);
  const [isRenameDashboard, setRenameDashboard] = useState(false);
  const [isDashboardCardVisible, setDashboardCardVisible] = useState(false);
  const [dashboardCardRef, setDashboardCardRef] = useState<
    React.RefObject<HTMLDivElement>
  >(React.createRef());
  const [activeView, setActiveView] = useState<'sheet' | 'dashboard'>('sheet');
  const [isChartModalVisible, setChartModalVisible] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<FilterRule[]>([]);
  const [filterLogicRelation, setFilterLogicRelation] = useState<'and' | 'or'>(
    'and'
  );

  // 修改筛选相关状态 - 每个视图独立的筛选状态
  const [viewFiltersState, setViewFiltersState] = useState<
    Record<
      string,
      {
        filters: FilterRule[];
        logicRelation: 'and' | 'or';
      }
    >
  >({
    default: {filters: [], logicRelation: 'and'}
  });
  // 添加视图相关状态
  const [views, setViews] = useState<ViewConfig[]>([
    {
      id: 'default',
      name: '全部数据',
      filters: [],
      logicRelation: 'and',
      isDefault: true
    }
  ]);
  const [activeViewId, setActiveViewId] = useState('default');
  // 修改视图相关状态 - 每个collection都有独立的视图集合
  const [collectionViews, setCollectionViews] = useState<
    Record<
      string,
      {
        views: ViewConfig[];
        activeViewId: string;
        viewFiltersState: Record<
          string,
          {filters: FilterRule[]; logicRelation: 'and' | 'or'}
        >;
      }
    >
  >({});

  // Refs
  // 创建一个 ref 来持有所有 state 和 props 的最新值，解决闭包问题
  const stateRef = useRef({
    // 将所有需要在回调中使用的 state 和 props 放入
    allSheetList,
    currentSheet,
    currentSheetPermissions,
    selectedCollection,
    focusCollection,
    focusSheet,
    focusSheetPermissions,
    focusSheetPermissionIds,
    currentCol,
    currentRow,
    userId,
    direction,
    // --- 仪表盘 Ref State ---
    selectedDashboard,
    focusDashboard,
    isRenameDashboard
  });
  const tableInstance = useRef<VTable.ListTable | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionRef = useRef<VTable.ListTableConstructorOptions>({});
  const selectedCollectionRef = useRef(selectedCollection);
  const sheetCardRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const columnsRef = useRef<VTable.ColumnDefine[]>([]);
  const recordsRef = useRef<any[]>([]);
  const recordsSnapshotRef = useRef<any[]>([]);
  const selectCellsRef = useRef<any[]>([]);
  const dragFieldIndexRef = useRef<number>(-1);

  // --- Permissions ---
  const ensureSheetPolicy = useCallback(
    async (policyName: string) => {
      if (!api) return null;
      try {
        const policies = await api.getPolicies({fields: ['id', 'name']});
        let policy = policies.find((p: any) => p.name === policyName);
        if (!policy) {
          policy = await api.createPolicy({
            name: policyName,
            description:
              policyName !== '智能表格'
                ? `用户${userInfo.last_name}${userInfo.first_name}的访问策略`
                : '',
            admin_access: false,
            app_access: true
          });
        }
        return policy;
      } catch (error: any) {
        message.error(`获取策略失败: ${error?.errors?.[0]?.message}`);
        return null;
      }
    },
    [api, userInfo]
  );

  const handleGetSheetPermissions = useCallback(
    async (collection: string) => {
      if (!api) return [];
      try {
        const policy = await ensureSheetPolicy('智能表格');
        if (!policy) return [];
        const allPermissions = await api.getPermissions({
          'filter[_and][0][policy]': policy.id,
          'filter[_and][0][collection]': collection,
          'fields': ['*']
        });
        const actions = allPermissions.map((item: any) => item.action);
        const ids = allPermissions.map((item: any) => item.id);
        console.log('获取权限列表:', actions, ids);

        setFocusSheetPermissions(actions);
        setFocusSheetPermissionIds(ids);
        return actions;
      } catch (error) {
        message.error('获取权限失败');
        return [];
      }
    },
    [api, ensureSheetPolicy]
  );

  /**
   * 根据选择批量创建权限项，
   * permissionType: 'all'|'edit'|'read'|'none'
   */
  const handleSetSheetPermissions = useCallback(
    async (collection: string, type: 'all' | 'edit' | 'read' | 'none') => {
      const {focusSheetPermissionIds, focusSheetPermissions} = stateRef.current;
      if (!api || !collection) return;
      try {
        const policy = await ensureSheetPolicy('智能表格');
        if (!policy) return;

        // 对应的 action 列表
        const map: Record<string, string[]> = {
          all: ['create', 'read', 'update', 'delete'],
          edit: ['read', 'update'],
          read: ['read'],
          none: []
        };
        const deleteIds = focusSheetPermissionIds.filter(
          (item: any, index: number) =>
            !map[type].includes(focusSheetPermissions[index])
        );
        const createActions = map[type].filter(
          (item: string) => !focusSheetPermissions.includes(item)
        );

        let createList: any[] = [];
        if (createActions.length > 0) {
          // 添加需要的权限
          createList = createActions.map((action: string) => {
            return {
              policy: policy.id,
              collection: collection,
              action,
              permissions: null,
              validation: null,
              fields: ['*'],
              presets: null
            };
          });
        }
        const actions = map[type] || [];
        await api.updatePolicy(policy.id, {
          permissions: {
            create: createList,
            delete: deleteIds
          }
        } as any);
        setFocusSheetPermissions(actions);
        message.success('权限设置成功');
      } catch (err: any) {
        message.error(`设置权限失败: ${err?.errors?.[0]?.message}`);
      }
    },
    [api, focusCollection, focusSheetPermissionIds, ensureSheetPolicy]
  );

  const handleSetCurrentUserPermissions = useCallback(async () => {
    if (!api || !focusCollection || !userId) return;
    try {
      const policy = await ensureSheetPolicy(userId);
      if (!policy) return;
      // 当前用户访问策略是否有聚焦表格的权限
      const allPermissions = await api.getPermissions({
        'filter[_and][0][policy]': policy.id,
        'filter[_and][0][collection]': focusCollection,
        'fields': ['*'] // 允许所有字段
      });
      if (allPermissions.length > 0) {
        // 如果已经有权限，则不再重复添加
        return;
      }
      const createActions = ['create', 'read', 'update', 'delete'];
      // 添加所有权限
      const createListSheet = createActions.map((action: string) => {
        return {
          policy: policy.id,
          collection: focusCollection,
          action,
          permissions: null,
          validation: null,
          fields: ['*'],
          presets: null
        };
      });
      await api.updatePolicy(policy.id, {
        permissions: {
          create: createListSheet
        },
        users: {
          create: [
            {
              policy: policy.id,
              user: {
                id: userId
              }
            }
          ]
        }
      } as any);
    } catch (error) {
      message.error('权限设置失败');
    }
  }, [api, focusCollection, userId, ensureSheetPolicy]);

  const onSetSheetPermissions = useCallback(
    async (type: 'all' | 'edit' | 'read' | 'none') => {
      // const {focusCollection, focusSheetPermissions} = stateRef.current;
      // console.log(
      //   '主组件设置权限:',
      //   focusSheetPermissions,
      //   type,
      //   focusCollection
      // );
      if (!focusCollection) {
        return;
      }

      if (
        (focusSheetPermissions.length === 4 && type === 'all') ||
        (focusSheetPermissions.length === 0 && type === 'none') ||
        (focusSheetPermissions.length === 1 && type === 'read') ||
        (focusSheetPermissions.length === 3 && type === 'edit')
      ) {
        // 如果已经有对应的权限，则不再重复添加
        return;
      }
      setSheetCardVisible(false);
      handleSetSheetPermissions(focusCollection, type);
      handleSetCurrentUserPermissions(); // 同时设置当前用户的访问策略
    },
    [
      api,
      focusCollection,
      handleSetSheetPermissions,
      handleSetCurrentUserPermissions
    ]
  );

  const hasPermission = useCallback(() => {
    const {currentSheetPermissions, currentSheet, userId} = stateRef.current;
    return (
      currentSheetPermissions.includes('create') ||
      currentSheet?.createUserId === userId
    );
  }, [stateRef]);

  // --- 图片和文件编辑器 ---
  // 图片预览功能
  const showImagePreview = (imageUrl: string) => {
    const modal = document.createElement('div');
    modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    cursor: pointer;
  `;

    const img = document.createElement('img');
    img.src = imageUrl;
    img.style.cssText = `
    max-width: 90%;
    max-height: 90%;
    object-fit: contain;
    border-radius: 8px;
  `;

    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '×';
    closeBtn.style.cssText = `
    position: absolute;
    top: 20px;
    right: 20px;
    background: rgba(255, 255, 255, 0.9);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    font-size: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

    closeBtn.addEventListener('click', e => {
      e.stopPropagation();
      document.body.removeChild(modal);
    });

    modal.addEventListener('click', () => {
      document.body.removeChild(modal);
    });

    modal.appendChild(img);
    modal.appendChild(closeBtn);
    document.body.appendChild(modal);
  };

  // 文件列表查看功能
  const showFileList = (
    files: Array<{url: string; name: string; size: number}>
  ) => {
    const modal = document.createElement('div');
    modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
  `;

    const container = document.createElement('div');
    container.style.cssText = `
    background: white;
    border-radius: 8px;
    padding: 20px;
    max-width: 500px;
    max-height: 400px;
    overflow-y: auto;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  `;

    const header = document.createElement('div');
    header.style.cssText = `
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
  `;

    const title = document.createElement('h3');
    title.textContent = `文件列表 (${files.length} 个文件)`;
    title.style.cssText = 'margin: 0; color: #333;';

    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '×';
    closeBtn.style.cssText = `
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #666;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

    closeBtn.addEventListener('click', () => {
      document.body.removeChild(modal);
    });

    header.appendChild(title);
    header.appendChild(closeBtn);

    const fileList = document.createElement('div');
    files.forEach(file => {
      const fileItem = document.createElement('div');
      fileItem.style.cssText = `
      display: flex;
      align-items: center;
      padding: 8px;
      margin-bottom: 4px;
      border: 1px solid #f0f0f0;
      border-radius: 4px;
      background: #fafafa;
    `;

      const fileIcon = document.createElement('span');
      fileIcon.textContent = getFileIcon(file.name);
      fileIcon.style.cssText = 'font-size: 20px; margin-right: 8px;';

      const fileDetails = document.createElement('div');
      fileDetails.style.cssText = 'flex: 1; overflow: hidden;';

      const fileName = document.createElement('div');
      fileName.textContent = file.name;
      fileName.style.cssText = `
      font-size: 14px;
      font-weight: 500;
      color: #333;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 2px;
    `;

      const fileSize = document.createElement('div');
      fileSize.textContent = formatFileSize(file.size);
      fileSize.style.cssText = 'font-size: 12px; color: #666;';

      const downloadBtn = document.createElement('button');
      downloadBtn.textContent = '下载';
      downloadBtn.style.cssText = `
      padding: 4px 12px;
      border: 1px solid #1890ff;
      border-radius: 4px;
      background: white;
      color: #1890ff;
      cursor: pointer;
      font-size: 12px;
    `;

      downloadBtn.addEventListener('click', () => {
        const a = document.createElement('a');
        a.href = file.url;
        a.download = file.name;
        a.click();
      });

      fileDetails.appendChild(fileName);
      fileDetails.appendChild(fileSize);

      fileItem.appendChild(fileIcon);
      fileItem.appendChild(fileDetails);
      fileItem.appendChild(downloadBtn);
      fileList.appendChild(fileItem);
    });

    modal.addEventListener('click', e => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    });

    container.appendChild(header);
    container.appendChild(fileList);
    modal.appendChild(container);
    document.body.appendChild(modal);
  };

  // 获取文件图标
  const getFileIcon = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const iconMap: Record<string, string> = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      xls: '📊',
      xlsx: '📊',
      ppt: '📋',
      pptx: '📋',
      txt: '📄',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      gif: '🖼️',
      zip: '🗂️',
      rar: '🗂️'
    };
    return iconMap[ext || ''] || '📎';
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // --- Data Fetching and Table Updates ---

  // 预处理图片数据：为每个图片字段创建一个 URL 获取函数
    const createImageUrlGetter = (fieldName: string) => {
      const urlCache = new Map<string, string>();
      
      return async (fieldValue: any): Promise<string> => {
        if (!fieldValue || !fieldValue.length) return '';
        
        const imageId = typeof fieldValue === 'string' ? fieldValue : fieldValue[0]?.directus_files_id || fieldValue;
        if (!imageId) return '';
        
        // 检查缓存
        if (urlCache.has(imageId)) {
          return urlCache.get(imageId)!;
        }
        
        try {
          const fullUrl = props.apiUrl + '/assets/' + imageId;
          const response = await fetch(fullUrl, {
            headers: { Authorization: `Bearer ${props.adminToken}` }
          });
          
          if (!response.ok) {
            console.error(`HTTP ${response.status} when fetching image: ${fullUrl}`);
            return '';
          }
          
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          
          // 缓存结果
          urlCache.set(imageId, blobUrl);
          
          return blobUrl;
        } catch (error) {
          console.error('获取图片失败:', error);
          return '';
        }
      };
    };
  const handleGetColumns = async (
    collection: string
  ): Promise<VTable.ColumnDefine[]> => {
    if (!api) return [];
    let fixedColumns: VTable.ColumnDefine[] = [];
    try {
      // const presets = await api.getPresets();
      // const existingPreset = presets.find(
      //   (item: any) => item.collection === collection
      // );
      const preset = await api.getPreset(collection);
      const existingPreset = preset?.[0];
      let fieldsInfo = await api.getFields(collection);
      if (existingPreset && 'id' in existingPreset) {
        const fieldsArray: string[] =
          existingPreset.layout_query?.tabular?.fields || [];
        const ordered = fieldsArray
          .map(name => fieldsInfo.find(item => item.field === name))
          .filter(Boolean) as Record<string, any>[];
        const rest = fieldsInfo.filter(
          item => !fieldsArray.includes(item.field)
        );
        fieldsInfo = [...ordered, ...rest];
      }
      const showFixedColumns = fieldsInfo.filter((item: any) => !item.meta?.hidden);

      fixedColumns = fieldsInfo
        .filter((item: any) => !item.meta?.hidden)
        .map((item: any) => {
      // for(const item of showFixedColumns) {
          console.log('item111111', item);
          const column: VTable.ColumnDefine = {
            title: item.meta?.translations?.[0]?.translation || item.field,
            field: item.field,
            width: Number(item.meta?.width) || 150,
            sort: true,
            headerIcon: [
              {
                type: 'svg',
                svg: svgIconMap[cellTypeMap[item.meta.interface==='file'?'file':item.type]] || svgIconMap.text,
                width: 20,
                height: 20,
                name: item.field,
                positionType: VTable.TYPES.IconPosition.contentLeft,
                visibleTime: 'always'
              }
            ],
            keepAspectRatio: true,
          };

          if (cellTypeMap[item.type] === 'list') {
            const choices = item.meta?.options?.choices || [];
            column.editor = new ListEditor({
              values: choices.map((c: any) => c.value)
            });
          } else if (item.meta.interface === 'file-image') {
            // column.cellType = 'image' as any;
            column.editor = 'image-editor';
            // 为当前字段创建专用的图片URL获取器
            const getImageUrl = createImageUrlGetter(item.field);
            // 使用自定义布局而不是 cellType: 'image'
            column.customLayout = (args: any) => {
              const { table, row, col, rect } = args;
              const record = table.getCellOriginRecord(col, row);
              const fieldValue = record[item.field];
              const { height, width } = rect ?? table.getCellRect(col, row);
              // 创建容器
              const container = createGroup({
                height,
                width,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                boundsPadding: [5, 5, 5, 5]
              });

              // 如果有图片数据
              if (fieldValue) {
                // 创建图片容器（左侧）
                const imageContainer = createGroup({
                  width: width - 35,
                  height: height - 10,
                  cursor: 'pointer'
                });
                // 先显示加载占位符
                const placeholderElement = createImage({
                  width: Math.min(width - 35, height - 10),
                  height: height - 10,
                  image: 'data:image/svg+xml;base64,' + btoa(`
                    <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
                      <rect width="100%" height="100%" fill="#f5f5f5"/>
                      <text x="50%" y="50%" text-anchor="middle" dy="0.3em" fill="#999">Loading...</text>
                    </svg>
                  `),
                  cornerRadius: 4
                });

                imageContainer.add(placeholderElement);
                container.add(imageContainer);
                // 异步加载真实图片
                getImageUrl(fieldValue)
                  .then(blobUrl => {
                    if (blobUrl) {
                      // 更新图片元素
                      placeholderElement.setAttribute('image', blobUrl);
                      
                      // 添加图片点击事件
                      placeholderElement.addEventListener('click', () => {
                        showImagePreview(blobUrl);
                      });
                    } else {
                      // 显示加载失败状态
                      placeholderElement.setAttribute('image', 'data:image/svg+xml;base64,' + btoa(`
                        <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
                          <rect width="100%" height="100%" fill="#ffebee"/>
                          <text x="50%" y="50%" text-anchor="middle" dy="0.3em" fill="#f44336">加载失败</text>
                        </svg>
                      `));
                    }
                    
                    // 刷新表格渲染
                    table.scenegraph.updateNextFrame();
                  })
                  .catch(error => {
                    console.error('图片加载失败:', error);
                    
                    // 显示错误状态
                    placeholderElement.setAttribute('image', 'data:image/svg+xml;base64,' + btoa(`
                      <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
                        <rect width="100%" height="100%" fill="#ffebee"/>
                        <text x="50%" y="50%" text-anchor="middle" dy="0.3em" fill="#f44336">加载失败</text>
                      </svg>
                    `));
                    
                    table.scenegraph.updateNextFrame();
                  });
              } 
              // else {
              //   // 没有图片时显示占位符
              //   const placeholderContainer = createGroup({
              //     width: width - 35,
              //     height: height - 10,
              //     display: 'flex',
              //     alignItems: 'center',
              //     justifyContent: 'center',
              //     fill: '#f5f5f5',
              //     cornerRadius: 4
              //   });

              //   const placeholderText = createText({
              //     text: '无图片',
              //     fontSize: 12,
              //     fill: '#999',
              //     textAlign: 'center'
              //   });

              //   placeholderContainer.add(placeholderText);
              //   container.add(placeholderContainer);
              // }

              // 创建上传按钮（右侧三角图标）
              // const uploadButton = createGroup({
              //   width: 25,
              //   height: 25,
              //   cursor: 'pointer',
              //   // fill: '#1890ff',
              //   cornerRadius: 12,
              //   display: 'flex',
              //   alignItems: 'center',
              //   justifyContent: 'center'
              // });

              // // 三角图标 SVG
              // const triangleSvg = `
              //   <svg t="1755488558926" class="icon" viewBox="0 0 1025 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="7336" width="200" height="200"><path d="M512.699 1024C230.027 1024 1 797.387 1 513.21 1 229.039 230.027 0 512.699 0 795.369 0 1025 230.854 1025 515.031S795.972 1024 512.699 1024z m0-964.03c-247.716 0-452.03 205.688-452.03 455.065 0 249.372 205.521 448.994 452.633 448.994s451.426-200.223 451.426-448.994c0-249.377-204.315-455.065-452.029-455.065z m34.954 604.514c-7.834 7.836-18.682 15.067-28.925 13.262-10.25 1.804-19.889-6.03-27.725-13.262L246.3 419.183c-12.657-12.658-12.657-32.548 0-45.204 12.658-12.653 32.548-12.653 45.206 0l227.819 233.849 228.429-233.849c12.658-12.653 32.548-12.653 45.2 0 12.658 12.657 12.658 32.546 0 45.204L547.653 664.484z m0 0" p-id="7337"></path></svg>
              // `;

              // const triangleIcon = createImage({
              //   width: 12,
              //   height: 12,
              //   image: 'data:image/svg+xml;base64,' + btoa(triangleSvg)
              // });

              // uploadButton.add(triangleIcon);

              // // 上传按钮点击事件
              // uploadButton.addEventListener('click', (event: any) => {
              //   event.stopPropagation();
              //   // 触发文件上传
              //   handleImageUpload(row, col, item.field);
              // });

              // container.add(uploadButton);

              return {
                rootContainer: container,
                renderDefault: false
              };
            };
            
          } else if (item.meta.interface === 'file') {
            column.editor = 'file-editor';
          } else {
            column.editor = `${cellTypeMap[item.type]}-editor`;
          }
          return column;
          fixedColumns.push(column);
        // }
        });
    } catch (err: any) {
      message.error(`获取字段失败: ${err?.errors?.[0]?.message || '未知错误'}`);
    }

    fixedColumns.push({
      field: 'addColumn',
      width: 45,
      headerIcon: {
        type: 'svg',
        svg: svgIconMap['add'],
        width: 20,
        height: 20,
        name: 'addColumn',
        positionType: VTable.TYPES.IconPosition.contentLeft,
        visibleTime: 'always'
      },
      disableColumnResize: true
    });
    const fields = fixedColumns
      .filter(col => col.field && col.field !== 'addColumn')
      .map(col => ({
        field: col.field as string,
        title: col.title as string,
        visible: true
      }));
    setFieldList(fields);
    return fixedColumns;
  };

  

  const getFieldArr = () => {
    const fieldNames = columnsRef.current
      .map(col => col.field)
      .filter((f): f is string => Boolean(f) && f !== 'addColumn');
    fieldNames.push('id');
    return fieldNames;
  };

  // const handleGetRecords = useCallback(
  //   async (collection: string) => {
  //     if (!api) return [];
  //     try {
  //       const fieldNames = getFieldArr();
  //       return await api.getRecords(collection, fieldNames);
  //     } catch (err: any) {
  //       message.error(
  //         `获取数据失败: ${err?.errors?.[0]?.message || '未知错误'}`
  //       );
  //     }
  //     return [];
  //   },
  //   [api]
  // );

  const handleGetRecords = useCallback(
  async (collection: string) => {
    if (!api) return [];
    try {
      const fieldNames = getFieldArr();
      const rawRecords = await api.getRecords(collection, fieldNames);
      
      // 处理图片和文件列的数据
      // let processedRecords = [];
      // // 使用 for...of 循环替代 map 以支持 async/await
      // for (const record of rawRecords) {
      //   const processedRecord = {...record};
        
      //   // 遍历所有列，找出图片和文件列
      //   for (const column of columnsRef.current) {
      //     if (!column.field || column.field === 'addColumn') continue;
      //     const field = column.field as string;
      //     const fieldValue = record[field];
      //     console.log('处理字段:', column, '值:', fieldValue);
          
      //     // 处理图片列 - 返回实际的图片URL用于显示
      //     if (column.cellType === 'image') {
      //       if (fieldValue) {
      //         processedRecord[field] = props.apiUrl + '/assets/' + fieldValue;
      //         const fullUrl = processedRecord[field];

      //         try {
      //           const response = await fetch(fullUrl, {
      //             headers: {Authorization: `Bearer ${props.adminToken}`}
      //           });

      //           if (!response.ok) {
      //             console.error(`HTTP ${response.status} when fetching image: ${fullUrl}`);
      //             // 如果获取失败，保留原始URL
      //             continue;
      //           }

      //           const blob = await response.blob();
      //           const blobUrl = URL.createObjectURL(blob);
      //           processedRecord[field] = blobUrl;
      //           console.log('处理图片列:', processedRecord[field]);
      //         } catch (error) {
      //           console.error('获取图片失败:', error);
      //           // 如果获取失败，保留原始URL
      //         }
      //       }
      //     }
      //     // 处理文件列 - 保持原有的文本显示逻辑
      //     else if (column.editor === 'file-editor') {
      //       if (fieldValue && Array.isArray(fieldValue) && fieldValue.length > 0) {
      //         const fileCount = fieldValue.length;
      //         processedRecord[field] = `📎 ${fileCount}个文件，点击查看`;
      //       } else if (fieldValue && typeof fieldValue === 'object' && fieldValue.id) {
      //         processedRecord[field] = '📎 1个文件，点击查看';
      //       } else {
      //         // processedRecord[field] = '点击上传文件';
      //         processedRecord[field] = '';
      //       }
      //     }
      //   }
        
      //   processedRecords.push(processedRecord);
      // }
      
      // return processedRecords;
      return rawRecords;
    } catch (err: any) {
      message.error(
        `获取数据失败: ${err?.errors?.[0]?.message || '未知错误'}`
      );
    }
    return [];
  },
  [api, props.apiUrl, props.adminToken]
);

  const updateTableRows = useCallback(
    async (collection: string) => {
      const newRecords = await handleGetRecords(collection);
      if (newRecords) {
        recordsRef.current = newRecords;
        tableInstance.current?.setRecords(newRecords);
      }
    },
    [handleGetRecords]
  );

  const updateTableColumns = useCallback(
    async (collection: string) => {
      const newColumns = await handleGetColumns(collection);
      columnsRef.current = newColumns;
      tableInstance.current?.updateColumns(newColumns);
    },
    [handleGetColumns]
  );

  // 获取当前collection的视图状态 - 现在从后端获取
  const getCurrentCollectionViewState = useCallback(async () => {
    const {selectedCollection} = stateRef.current;
    if (!selectedCollection || !api) {
      return {
        views: [
          {
            id: 'default',
            name: '全部数据',
            filters: [],
            logicRelation: 'and' as const,
            isDefault: true
          }
        ],
        activeViewId: 'default',
        viewFiltersState: {
          default: {filters: [], logicRelation: 'and' as const}
        }
      }
    }

    try {
      // 查询当前用户对该collection的所有视图预设
      const presets = await api.getPresetsDetail({
        'filter[collection]': selectedCollection,
        'filter[user]': userId
      });

      let views: ViewConfig[] = [];
      let viewFiltersState: Record<
        string,
        {filters: FilterRule[]; logicRelation: 'and' | 'or'}
      > = {};

      // 转换后端预设为前端视图格式
      presets.forEach((preset: any) => {
        const filters = preset.filter || [];
        const isDefault = preset.bookmark === 'default';

        views.push({
          id: preset.bookmark, // 使用title作为视图id
          name: preset.layout, // 使用layout作为视图名称
          filters: filters.map((f: any) => ({
            field: f.field,
            operator: f.operator,
            value: f.value
          })),
          logicRelation: 'and', // 默认使用and逻辑
          isDefault
        });

        viewFiltersState[preset.bookmark] = {
          filters: filters.map((f: any) => ({
            field: f.field,
            operator: f.operator,
            value: f.value
          })),
          logicRelation: 'and'
        };
      });

      // 如果没有默认视图，创建一个
      if (!views.find(v => v.isDefault)) {
        const defaultView = {
          id: 'default',
          name: '全部数据',
          filters: [],
          logicRelation: 'and' as const,
          isDefault: true
        };
        views.unshift(defaultView);
        viewFiltersState.default = {filters: [], logicRelation: 'and'};
      }

      return {
        views,
        activeViewId:
          collectionViews[selectedCollection]?.activeViewId || 'default',
        viewFiltersState
      };
    } catch (error) {
      console.error('获取视图失败:', error);
      // 出错时返回默认状态
      return {
        views: [
          {
            id: 'default',
            name: '全部数据',
            filters: [],
            logicRelation: 'and' as const,
            isDefault: true
          }
        ],
        activeViewId: 'default',
        viewFiltersState: {
          default: {filters: [], logicRelation: 'and' as const}
        }
      };
    }
  }, [selectedCollection, api, userId, collectionViews]);

  const useDebounceCallback = (
    callback: Function,
    delay: number,
    deps: any[]
  ) => {
    const timeoutRef = useRef<any>();

    return useCallback(
      (...args: any[]) => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          callback(...args);
        }, delay);
      },
      [...deps, delay]
    );
  };

  // --- 表格更新函数 ---
  const handleUpdateTable = useCallback(
    async (collection: string) => {
      // 防止重复调用
      if (selectedCollectionRef.current === collection && !loading) {
        console.log('跳过重复调用:', collection);
        return;
      }

      console.log('开始更新表格:', collection);
      setLoading(true);
      selectedCollectionRef.current = collection;

      try {
        // 同步更新基础状态
        setSelectedCollection(collection);
        setAppliedFilters([]);
        setFilterLogicRelation('and');

        // 并行获取数据
        const [newColumns, perms] = await Promise.all([
          handleGetColumns(collection),
          handleGetSheetPermissions(collection)
        ]);

        columnsRef.current = newColumns;
        const newRecords = await handleGetRecords(collection);
        recordsRef.current = newRecords || [];

        // 更新表格
        tableInstance.current?.updateOption({
          ...optionRef.current,
          columns: newColumns,
          records: newRecords
        });

        const sheet =
          allSheetList.find(s => s.collection === collection) || null;
        setCurrentSheet(sheet);
        setCurrentSheetPermissions(perms);

        // 获取或创建视图状态
        try {
          const viewState = await getCurrentCollectionViewState();
          console.log('获取到视图状态:', viewState);

          // 确保viewFiltersState与views中的配置同步
          const syncedViewFiltersState: Record<
            string,
            {filters: FilterRule[]; logicRelation: 'and' | 'or'}
          > = {};
          viewState.views.forEach(view => {
            syncedViewFiltersState[view.id] = {
              filters: view.filters,
              logicRelation: view.logicRelation
            };
          });

          const syncedViewState = {
            ...viewState,
            viewFiltersState: {
              ...viewState.viewFiltersState,
              ...syncedViewFiltersState
            }
          };

          // 批量更新视图状态
          unstable_batchedUpdates(() => {
            setCollectionViews(prev => ({
              ...prev,
              [collection]: syncedViewState
            }));
          });
        } catch (viewError) {
          console.error('获取视图状态失败:', viewError);
          // 创建默认视图状态
          const defaultViewState = {
            views: [
              {
                id: 'default',
                name: '全部数据',
                filters: [] as FilterRule[],
                logicRelation: 'and' as const,
                isDefault: true
              }
            ],
            activeViewId: 'default',
            viewFiltersState: {
              default: {
                filters: [] as FilterRule[],
                logicRelation: 'and' as const
              }
            }
          };

          setCollectionViews(prev => ({
            ...prev,
            [collection]: defaultViewState
          }));
        }
      } catch (error) {
        console.error('更新表格失败:', error);
      } finally {
        setLoading(false);
      }
    },
    [
      handleGetColumns,
      handleGetRecords,
      handleGetSheetPermissions,
      getCurrentCollectionViewState,
      allSheetList,
      loading
    ]
  );

  // 使用防抖的 handleUpdateTable
  const debouncedHandleUpdateTable = useDebounceCallback(
    handleUpdateTable,
    200, // 200ms 防抖
    [handleUpdateTable]
  );

  // --- 获取工作表列表的函数 ---
  const handleGetSheets = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const policy = await ensureSheetPolicy('智能表格');
      if (!policy) return;
      const allPermissionsModels = await api.getPermissions({
        filter: {
          _and: [
            {
              policy: policy.id
            },
            {
              collection: {
                _contains: 'grid_sheet_'
              }
            }
          ]
        },
        fields: ['*']
      });
      // 把allPermissions数组根据collection去重
      const uniquePermissionsModels = allPermissionsModels.filter((models: any, index: number, self: any[]) => 
        self.findIndex(p => p.collection === models.collection) === index
      );
      
      const res = await api.getSheets();
      if (res && res.length > 0) {
        const sheets = res
          // .filter((item: any) => item.collection.includes('grid_sheet_'))
          .filter((item: any) => {
            // 筛选等于uniquePermissionsModels数组内collection的数据
            return uniquePermissionsModels.some((permission: any) => 
              permission.collection === item.collection
            );
          })
          .map((item: any) => ({
            name: item.meta?.translations?.[0].translation || item.collection,
            collection: item.collection,
            createUserId: item.meta?.note || '',
          }));

        setAllSheetList(sheets);
        setFilterSheetList(sheets);
        const {selectedCollection} = stateRef.current;
        const existingSheet = sheets.find(
          (item: any) => item.collection === selectedCollection
        );
        const initialCollection = existingSheet
          ? existingSheet.collection
          : sheets[0]?.collection;

        if (initialCollection) {
          setSelectedCollection(initialCollection);
          selectedCollectionRef.current = initialCollection;
          const [newColumns, perms] = await Promise.all([
            handleGetColumns(initialCollection),
            handleGetSheetPermissions(initialCollection)
          ]);

          columnsRef.current = newColumns;
          const newRecords = await handleGetRecords(initialCollection);
          recordsRef.current = newRecords || [];

          tableInstance.current?.updateOption({
            ...optionRef.current,
            columns: newColumns,
            records: newRecords
          });
          const sheet =
            sheets.find(s => s.collection === initialCollection) || null;
          setCurrentSheet(sheet);
          setCurrentSheetPermissions(perms);
        }
      }
    } catch (err: any) {
      message.error(
        `获取工作表列表失败: ${err?.errors?.[0]?.message || '未知错误'}`
      );
    } finally {
      setLoading(false);
    }
  }, [api, handleGetColumns, handleGetRecords, handleGetSheetPermissions]);

  // --- Event Handlers ---
  // 用于计算卡片位置
  const calculateCardPosition = useCallback(
    (left: number, top: number, type: string) => {
      // FieldEditorCard的大致尺寸
      const CARD_WIDTH = 300;
      const CARD_HEIGHT = 400;

      // 获取视窗尺寸
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // 获取表格容器的边界信息，用于计算可用空间
      let containerRect = {
        left: 0,
        top: 0,
        right: viewportWidth,
        bottom: viewportHeight
      };
      if (containerRef.current && type === 'content') {
        containerRect = containerRef.current.getBoundingClientRect();
      }
      // 调整left位置，确保卡片不会超出表格容器的右边界
      if (type === 'content') {
        if (left + CARD_WIDTH > containerRect.right) {
          left = left - CARD_WIDTH - containerRect.left; // 右边留10px边距
        } else {
          left = left - containerRect.left; // 确保left是相对于容器的
        }
      }

      // 调整top位置，确保卡片不会超出视窗底部
      if (top + CARD_HEIGHT < viewportHeight) {
        top = top - containerRect.top;
      } else {
        top = top - 150;
      }

      // 确保卡片不会超出视窗顶部
      if (top < 0) {
        top = 10; // 顶部留10px边距
      }

      return {left, top};
    },
    []
  );

  const onSelectSheet = (collection: string) => {
    if (collection !== selectedCollection) {
      console.log('选择工作表:', collection);
      tableInstance.current?.clearSelected();
      handleUpdateTable(collection);
    }
    setActiveView('sheet');
    setSelectedDashboard(null);
  };

  const onAddRow = async (position: string = 'notLastRow') => {
    const {currentSheet, selectedCollection, direction, currentRow} =
      stateRef.current;
    if (!api || !currentSheet) return;
    if (!hasPermission()) {
      message.error('没有权限进行此操作');
      return;
    }
    const newRow: Record<string, any> = {};
    columnsRef.current.forEach(col => {
      if (
        !col.field ||
        typeof col.field !== 'string' ||
        col.field === 'addColumn'
      )
        return;
      const editorType =
        typeof col.editor === 'string' ? col.editor.split('-')[0] : 'list';
      newRow[col.field] = ['number', 'date', 'list'].includes(editorType)
        ? null
        : '';
    });

    try {
      const newRowIndex = direction === 'down' ? currentRow : currentRow - 1;
      const returnRow = await api.createRecord(selectedCollection, newRow);
      const fromId = returnRow.id;
      let toId = 0;
      if (position !== 'lastRow' && recordsRef.current[newRowIndex]) {
        toId = recordsRef.current[newRowIndex]?.id;
      } else {
        // 如果是最后一行，则直接添加到末尾
        toId = fromId;
      }
      await api.sortRecord(selectedCollection, fromId, toId);
      await updateTableRows(selectedCollection);
    } catch (err: any) {
      message.error(`新增行失败: ${err?.errors?.[0]?.message}`);
    }
  };

  const onDeleteRows = async () => {
    const {selectedCollection} = stateRef.current;
    if (!api || !selectedCollection || selectCellsRef.current.length === 0)
      return;

    if (!hasPermission()) {
      message.error('没有权限进行此操作');
      return;
    }

    try {
      const rowIds = selectCellsRef.current.map(cell => cell[0].originData.id);
      await Promise.all(
        rowIds.map(id => api.deleteRecord(selectedCollection, id))
      );
      await updateTableRows(selectedCollection);
      selectCellsRef.current = [];
      tableInstance.current?.clearSelected();
    } catch (err: any) {
      message.error(`删除行失败: ${err?.errors?.[0]?.message}`);
    }
  };

  const onCreateSheet = async () => {
    if (!api) return;
    const timestamp = Date.now();
    const newCollection = `grid_sheet_${timestamp}`;
    // 当前日期时间，格式为 YYYY-MM-DD HH:mm:ss
    const now = `${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}`;
    const displayName = `智能表_${now}`;
    const collectionObject = {
      collection: newCollection, // 表名
      schema: {},
      fields: [
        {
          field: 'id',
          type: 'integer',
          meta: {
            hidden: true,
            interface: 'input',
            readonly: true
          },
          schema: {
            is_primary_key: true,
            has_auto_increment: true
          }
        },
        {
          field: 'sort',
          type: 'integer',
          meta: {
            interface: 'input',
            hidden: true
          },
          schema: {}
        },
        {
          field: 'user_created',
          type: 'uuid',
          meta: {
            special: ['user-created'],
            interface: 'select-dropdown-m2o',
            options: {
              template: '{{avatar}} {{first_name}} {{last_name}}'
            },
            display: 'user',
            readonly: true,
            hidden: true,
            width: 'half'
          },
          schema: {}
        },
        {
          field: 'date_created',
          type: 'timestamp',
          meta: {
            special: ['date-created'],
            interface: 'datetime',
            readonly: true,
            hidden: true,
            width: 'half',
            display: 'datetime',
            display_options: {
              relative: true
            }
          },
          schema: {}
        },
        {
          field: 'user_updated',
          type: 'uuid',
          meta: {
            special: ['user-updated'],
            interface: 'select-dropdown-m2o',
            options: {
              template: '{{avatar}} {{first_name}} {{last_name}}'
            },
            display: 'user',
            readonly: true,
            hidden: true,
            width: 'half'
          },
          schema: {}
        },
        {
          field: 'date_updated',
          type: 'timestamp',
          meta: {
            special: ['date-updated'],
            interface: 'datetime',
            readonly: true,
            hidden: true,
            width: 'half',
            display: 'datetime',
            display_options: {
              relative: true
            }
          },
          schema: {}
        }
      ],
      meta: {
        // group: 'grid_sheet',
        sort_field: 'sort',
        singleton: false,
        versioning: true, // 是否开启版本控制
        note: userId, // 备注信息
        translations: [
          {
            language: 'zh-CN', // 或者你项目需要的语言码
            translation: displayName
          }
        ]
      }
    };
    try {
      // 1. 创建集合
      await api.createCollection(collectionObject);
      message.success('工作表创建成功');
      // 2. 设置权限
      // 集合创建完就需要把它加到“智能表格”访问策略中
      setFocusSheetPermissions([]);
      setFocusSheetPermissionIds([]);
      setFocusCollection(newCollection);
      await handleSetSheetPermissions(newCollection, 'all');

      // 3. 创建默认视图
      await createDefaultView(newCollection);

      // 4. 切换到新工作表
      setSelectedCollection(newCollection);
      await handleGetSheets();
      setActiveView('sheet');
      setSelectedDashboard(null);
    } catch (err: any) {
      message.error(`创建工作表失败: ${err?.errors?.[0]?.message}`);
    }
  };

  const onRenameSheet = async (newName: string) => {
    if (!api || !focusCollection) return;

    try {
      await api.renameSheet(focusCollection, newName);
      await handleGetSheets();
      setSheetCardVisible(false);
      setRenameSheet(false);
      message.success('重命名成功');
    } catch (err: any) {
      message.error(`重命名失败: ${err?.errors?.[0]?.message}`);
    }
  };
  const handleRenameConfirm = (
    e:
      | React.FocusEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>,
    collection: string
  ) => {
    const newName = (e.target as HTMLInputElement).value.trim();
    if (!newName) {
      message.error('工作表名称不能为空');
      return;
    }

    // 使用焦点集合而不是传入的 collection 参数（如果需要的话）
    setFocusCollection(collection);
    onRenameSheet(newName);
  };

  const onDeleteSheet = async () => {
    if (!api || !focusCollection) return;

    try {
      setLoading(true);
      setSheetCardVisible(false);
      await api.deleteCollection(focusCollection);
      await handleGetSheets();
      message.success('删除成功');
    } catch (err: any) {
      message.error(`删除失败: ${err?.errors?.[0]?.message}`);
    }
  };

  const onOperateSheet = (
    e: React.MouseEvent<HTMLElement>,
    collection: string
  ) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    const sheet = allSheetList.find(s => s.collection === collection) || null;
    const target = e.currentTarget as HTMLElement;
    let parent = target.offsetParent as HTMLElement;
    let parentRect = parent.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    setFocusCollection(collection);
    setFocusSheet(sheet);
    setSheetCardVisible(true);
    setDashboardCardVisible(false);
    setRenameSheet(false);
    const left = rect.left - parentRect.left + parent.scrollLeft;
    const top = rect.bottom - parentRect.top + parent.scrollTop;
    const adjustedPosition = calculateCardPosition(left, top, 'sider');
    setCardPosition(adjustedPosition);
  };

  const onConfirmField = async (values: any) => {
    if (!api) return;
    const {fieldName, fieldType, fieldOptions} = values;
    const processedFieldName = fieldName
      .toLowerCase()
      .replace(/[\s\/\\]+/g, '_') // 将空格、斜杠、反斜杠替换为下划线
      .replace(/[^\w\u4e00-\u9fa5]/g, '') // 移除除字母、数字、下划线和中文外的其他字符
      .replace(/_+/g, '_'); // 将多个连续下划线合并为一个;
    const payload: any = {
      field: processedFieldName,
      type: cellTypeMap[fieldType],
      meta: {
        interface:
          fieldType === 'textArea'
            ? 'input-multiline'
            : fieldType === 'image'
            ? 'file-image'
            : fieldType === 'file'
            ? 'file'
            : 'input',
        special: fieldType === 'image' || fieldType === 'file' ? ['file'] : [],
        translations: [{language: 'zh-CN', translation: fieldName}]
      }
    };

    if (fieldType === 'list') {
      payload.meta.options = {
        choices: fieldOptions.map((opt: string) => ({text: opt, value: opt}))
      };
      payload.meta.interface = 'select-dropdown';
    }

    try {
      if (operateType === 'add') {
        const newColIndex = direction === 'right' ? currentCol : currentCol - 1;

        const newColumn: VTable.ColumnDefine = {
          field: processedFieldName, // 新列的字段名
          title: fieldName, // 新列的标题
          width: 150, // 新列的宽度
          // editor: `${fieldType}-editor`, // 使用文本编辑器
          headerIcon: {
            type: 'svg', //指定svg格式图标，其他还支持path，image
            svg: svgIconMap[fieldType], // 使用对应类型的图标
            width: 20,
            height: 20,
            name: fieldType, //定义图标的名称，在内部会作为缓存的key值
            positionType: VTable.TYPES.IconPosition.contentLeft, // 指定位置，可以在文本的前后，或者在绝对定位在单元格的左侧右侧
            visibleTime: 'always'
          },
          cellType: 'text'
        };

        // 根据字段类型设置编辑器和渲染器
        if (fieldType === 'list') {
          newColumn.editor = new ListEditor({
            values: fieldOptions || []
          });
        } else if (fieldType === 'image') {
          newColumn.editor = 'image-editor';
        } else if (fieldType === 'file') {
          newColumn.editor = 'file-editor';
        } else {
          newColumn.editor = `${fieldType}-editor`;
        }
        await api.createField(selectedCollection, payload);
        columnsRef.current.splice(newColIndex, 0, newColumn);
        // 需要再调一下预设的接口
        const fieldNames = getFieldArr();
        await setColumnsPreset(fieldNames);
      } else {
        await api.updateField(selectedCollection, postFieldName, {
          meta: payload.meta
        });
      }
      await updateTableColumns(selectedCollection);
      setCardVisible(false);
    } catch (err: any) {
      message.error(`操作字段失败: ${err?.errors?.[0]?.message}`);
    }
  };

  const onDeleteField = async (fieldName: string) => {
    if (!api) return;
    const {selectedCollection} = stateRef.current;

    if (!hasPermission()) {
      message.error('没有权限进行此操作');
      return;
    }

    try {
      await api.deleteField(selectedCollection, fieldName);
      await updateTableColumns(selectedCollection);
      message.success('字段删除成功');
      tableInstance.current?.clearSelected();
    } catch (err: any) {
      message.error(`删除字段失败: ${err?.errors?.[0]?.message}`);
    }
  };

  // 导入Excel功能
  const onImportExcelFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      event.target.value = ''; // 重置以便可以再次选择同一个文件
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, {type: 'array'});
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1
        }) as any[][];

        if (jsonData.length === 0) {
          message.error('Excel文件为空或格式不正确');
          return;
        }

        const headers = jsonData[0] as string[]; // 第一行为表头
        const dataRows = jsonData.slice(1); // 其余为数据
        setImportHeaders(headers);
        setImportData(dataRows);
        setImportFileName(file.name);
        // 初始化列类型映射，默认为'text'
        setColumnTypeMappings(
          headers.reduce((acc, header) => ({...acc, [header]: 'text'}), {})
        );
        setImportModalVisible(true);
      } catch (error) {
        message.error('文件解析失败');
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  // 确认导入
  const onConfirmImport = async () => {
    setImportModalVisible(false);
    setLoading(true);
    if (!api || importData.length === 0) return;

    try {
      // 过滤已存在的字段
      const cleanFields = importHeaders.filter(
        (field: string) =>
          !fieldList.some(
            f =>
              f.field ===
              field
                .toLowerCase()
                .replace(/[\s\/\\]+/g, '_')
                .replace(/[^\w\u4e00-\u9fa5]/g, '')
                .replace(/_+/g, '_')
          )
      );
      console.log('cleanFields:', cleanFields, importHeaders, fieldList);

      // 创建字段
      for (const header of cleanFields) {
        const fieldType = columnTypeMappings[header] || 'text';
        const processedFieldName = header
          .toLowerCase()
          .replace(/[\s\/\\]+/g, '_') // 将空格、斜杠、反斜杠替换为下划线
          .replace(/[^\w\u4e00-\u9fa5]/g, '') // 移除除字母、数字、下划线和中文外的其他字符
          .replace(/_+/g, '_'); // 将多个连续下划线合并为一个;
        await api.createField(selectedCollection, {
          field: processedFieldName,
          type: cellTypeMap[fieldType],
          meta: {
            interface: fieldType === 'textArea' ? 'input-multiline' : 'input',
            translations: [{language: 'zh-CN', translation: header}]
          }
        });
      }

      // 插入数据
      for (const row of importData) {
        const record: any = {};
        importHeaders.forEach((header, index) => {
          const processedFieldName = header
            .toLowerCase()
            .replace(/[\s\/\\]+/g, '_') // 将空格、斜杠、反斜杠替换为下划线
            .replace(/[^\w\u4e00-\u9fa5]/g, '') // 移除除字母、数字、下划线和中文外的其他字符
            .replace(/_+/g, '_'); // 将多个连续下划线合并为一个;
          record[processedFieldName] = row[index] || null;
        });
        const returnRow = await api.createRecord(selectedCollection, record);
        await api.sortRecord(selectedCollection, returnRow.id, returnRow.id);
      }

      await handleUpdateTable(selectedCollection);
      message.success('导入成功');
    } catch (err: any) {
      message.error(`导入失败: ${err?.errors?.[0]?.message}`);
      setLoading(false);
    }
  };

  const onExportAsExcel = async () => {
    if (!tableInstance.current) return;
    setSheetCardVisible(false);
    await downloadExcel(
      await exportVTableToExcel(tableInstance.current, {
        ignoreIcon: true
      }),
      'export'
    );
  };

  const onExportAsCsv = async () => {
    if (!tableInstance.current) return;
    setSheetCardVisible(false);
    await downloadCsv(exportVTableToCsv(tableInstance.current), 'export');
  };

  // 创建副本
  const onCreateCopySheet = async () => {
    const {focusSheet, focusCollection} = stateRef.current;
    if (!api || !focusSheet || !focusCollection) {
      message.error('未选中任何工作表');
      return;
    }

    setLoading(true);
    setSheetCardVisible(false);

    try {
      // 1. 定义新副本的名称和标识
      const timestamp = Date.now();
      const newCollection = `grid_sheet_copy_${timestamp}`;
      const newDisplayName = `${focusSheet.name}(副本)`;

      // 2. 获取源表的字段定义
      const sourceFields = await api.getFields(focusCollection);
      const fieldsToCopy = sourceFields
        .filter(
          (field: any) =>
            // 过滤掉系统默认生成的主键和特殊字段以及排序字段
            !field.schema?.is_primary_key &&
            !field.meta?.special &&
            field.field !== 'sort'
        )
        .map(item => {
          return {
            field: item.field,
            type: item.type
          };
        });

      // 3. 创建新的集合（表结构）
      const collectionObject = {
        collection: newCollection,
        schema: {}, // 必须，不然创建的是文件夹
        meta: {
          //   group: 'grid_sheet',
          sort_field: 'sort',
          singleton: false,
          versioning: true,
          note: userId,
          translations: [{language: 'zh-CN', translation: newDisplayName}]
        },
        fields: [
          // 添加必要的系统字段
          {
            field: 'id',
            type: 'integer',
            meta: {hidden: true, interface: 'input', readonly: true},
            schema: {is_primary_key: true, has_auto_increment: true}
          },
          {
            field: 'sort',
            type: 'integer',
            meta: {interface: 'input', hidden: true}
          },
          {
            field: 'user_created',
            type: 'uuid',
            meta: {
              special: ['user-created'],
              interface: 'select-dropdown-m2o',
              options: {template: '{{avatar}} {{first_name}} {{last_name}}'},
              display: 'user',
              readonly: true,
              hidden: true,
              width: 'half'
            }
          },
          {
            field: 'date_created',
            type: 'timestamp',
            meta: {
              special: ['date-created'],
              interface: 'datetime',
              readonly: true,
              hidden: true,
              width: 'half',
              display: 'datetime',
              display_options: {relative: true}
            }
          },
          {
            field: 'user_updated',
            type: 'uuid',
            meta: {
              special: ['user-updated'],
              interface: 'select-dropdown-m2o',
              options: {template: '{{avatar}} {{first_name}} {{last_name}}'},
              display: 'user',
              readonly: true,
              hidden: true,
              width: 'half'
            }
          },
          {
            field: 'date_updated',
            type: 'timestamp',
            meta: {
              special: ['date-updated'],
              interface: 'datetime',
              readonly: true,
              hidden: true,
              width: 'half',
              display: 'datetime',
              display_options: {relative: true}
            }
          },
          // 添加从源表复制的字段
          ...fieldsToCopy
        ]
      };
      await api.createCollection(collectionObject);
      message.success('工作表副本创建成功');
      // 集合创建完就需要把它加到“智能表格”访问策略中
      setFocusSheetPermissions([]);
      setFocusSheetPermissionIds([]);
      setFocusCollection(newCollection);
      setSelectedCollection(newCollection);
      await handleSetSheetPermissions(newCollection, 'all');

      // 4. 复制源表数据
      const recordsToCopy = recordsRef.current.map(record => {
        const newRecord: Record<string, any> = {...record};
        delete newRecord.id; // 必须删除旧的id，让数据库自动生成
        // 清理掉所有系统字段，避免写入错误
        delete newRecord.user_created;
        delete newRecord.date_created;
        delete newRecord.user_updated;
        delete newRecord.date_updated;
        delete newRecord.sort;
        return newRecord;
      });

      if (recordsToCopy.length > 0) {
        await api.createRecords(newCollection, recordsToCopy);
      }

      // 5. 刷新工作表列表并切换到新副本
      await handleGetSheets();
    } catch (err: any) {
      message.error(
        `创建工作表副本失败: ${err?.errors?.[0]?.message || '未知错误'}`
      );
    } finally {
      setLoading(false);
    }
  };

  // 字段管理中的字段拖拽
  /** 拖拽开始 */
  const handleFieldDragStart =
    (index: number, field: string) => (e: React.DragEvent) => {
      dragFieldIndexRef.current = index;
      setDraggingField(field);
      // 创建一个透明的图像作为拖拽预览，以隐藏默认的预览图
      const img = new Image();
      img.src =
        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
      e.dataTransfer.setDragImage(img, 0, 0);
      e.dataTransfer.effectAllowed = 'move';
    };
  /** 拖拽结束（放下） */
  const handleFieldDrop = (index: number) => async (e: React.DragEvent) => {
    e.preventDefault();
    const currentDragIndex = dragFieldIndexRef.current;

    // 确保 dragFieldIndex 有效
    if (currentDragIndex < 0 || currentDragIndex >= fieldList.length) {
      setDraggingField(null);
      return;
    }

    // 重新排序字段列表
    const newFieldList = [...fieldList];
    const [moved] = newFieldList.splice(currentDragIndex, 1);
    newFieldList.splice(index, 0, moved);

    // 同步到 VTable 列顺序
    const newColumns = newFieldList
      .map(item => columnsRef.current.find(col => col.field === item.field)!)
      .filter(Boolean);

    // 添加 addColumn 列到末尾
    const addColumnCol = columnsRef.current.find(
      col => col.field === 'addColumn'
    );
    if (addColumnCol) {
      newColumns.push(addColumnCol);
    }

    // 更新状态和表格
    setFieldList(newFieldList);
    setDraggingField(null);
    columnsRef.current = newColumns;

    try {
      // 更新预设配置
      if (api) {
        const fieldNames = newFieldList.map(item => item.field);
        await setColumnsPreset(fieldNames);
      }

      // 更新表格列
      tableInstance.current?.updateColumns(newColumns);

      message.success('字段顺序已更新');
    } catch (err: any) {
      message.error(`更新字段顺序失败: ${err?.message}`);
    }
  };

  const setColumnsPreset = async (fieldNames: string[]) => {
    if (!api) return;

    try {
      // 获取现有预设
      // const presets = await api.getPresets();
      // const existingPreset = presets.find(
      //   (item: any) => item.collection === selectedCollection
      // );
      const preset = await api.getPreset(selectedCollection);
      const existingPreset = preset?.[0];

      const params = {
        // layout: null,
        // bookmark: null,
        // role: null,
        // user: userId,
        // search: null,
        // filter: null,
        layout_query: {
          tabular: {
            fields: fieldNames
          }
        }
        // layout_options: {
        //   tabular: {
        //     widths: {}
        //   }
        // },
        // refresh_interval: null,
        // icon: 'bookmark',
        // color: null,
        // collection: selectedCollection
      };

      if (existingPreset && 'id' in existingPreset) {
        // 更新预设
        await api.updatePreset(existingPreset.id, params);
      } else {
        // 创建预设
        await api.createPreset(params);
      }
    } catch (err: any) {
      message.error(
        `保存列顺序预设失败: ${err?.errors?.[0]?.message || '未知错误'}`
      );
    }
  };

  // --- 仪表盘 ---
  const handleGetDashboards = useCallback(async () => {
    if (!api) return;
    try {
      const res = await api.getDashboards();
      let dashboards: any[] = [];
      for (let item of res) {
        let permissionCode = 'NONE';
        let permissionId = null;
        try {
          // 步骤1: 查询用户特定权限
          const userPermissions = await api.getDashboardPermission(
            item.id,
            userId
          );

          if (userPermissions && userPermissions.length > 0) {
            // 用户有特定权限记录
            // const permissionId = userPermissions[0].permission_id;
            // if (permissionId) {
            //   const permission: any = await api.getPermission(permissionId);
            //   permissionCode = permission?.[0]?.code || 'NONE';
            // }
            permissionCode = userPermissions[0]?.permission_code || 'NONE';
            permissionId = userPermissions[0]?.id;
            console.log(
              '特定权限代码:',
              userPermissions[0]?.permission_code,
              permissionCode
            );
          } else {
            // 步骤2: 查询公共权限
            const publicPermissions = await api.getDashboardPermission(
              item.id,
              null
            );

            if (publicPermissions && publicPermissions.length > 0) {
              // 使用公共权限
              // const permissionId = publicPermissions[0].permission_id;
              // if (permissionId) {
              //   const permission: any = await api.getPermission(permissionId);
              //   permissionCode = permission?.[0]?.code || 'NONE';
              // }
              permissionCode = publicPermissions[0]?.permission_code || 'NONE';
              permissionId = publicPermissions[0]?.id;
              console.log(
                '公共权限代码:',
                publicPermissions[0]?.permission_code,
                permissionCode
              );
            }
          }
        } catch (err) {
          message.error(
            `获取仪表盘权限失败: ${err?.errors?.[0]?.message || '未知错误'}`
          );
          // 步骤3: 默认无权限
          permissionCode = 'NONE';
        }
        dashboards.push({
          id: item.id,
          name: item.name,
          createUserId: item.user_created,
          permission: permissionCode,
          permissionId: permissionId,
          generatedBy: item.generated_by
        });
      }

      // 保留有权限的仪表盘和用户自己创建的仪表盘，并去重
      const uniqueDashboards = dashboards.filter((item, index, self) => {
        // 保留有权限的仪表盘或用户自己创建的仪表盘
        const shouldKeep =
          item.permission !== 'NONE' || item.createUserId === userId;
        // 去重逻辑
        const isFirstOccurrence =
          self.findIndex(i => i.id === item.id) === index;
        return shouldKeep && isFirstOccurrence;
      });
      setAllDashboardList(uniqueDashboards);
      setFilterDashboardList(uniqueDashboards);
    } catch (err: any) {
      message.error(
        `获取仪表盘列表失败: ${err?.errors?.[0]?.message || '未知错误'}`
      );
    }
  }, [api]);

  const onCreateDashboard = async (type: 'common' | 'ai') => {
    if (!api) return;
    const now = `${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}`;
    let displayName = '';
    if (type === 'ai') {
      displayName = `${currentSheet?.name || '工作表'} - AI生成`;
    } else {
      displayName = `仪表盘_${now}`;
    }

    try {
      const res = await api.createDashboard({
        name: displayName,
        generated_by: type === 'ai' ? currentSheet?.collection : ''
      });
      // 设置公共权限
      await api.createDashboardPermission({
        dashboard_id: res.id,
        permission_code: 'ALL'
      });
      const dashboardId = res.id;
      message.success('仪表盘创建成功');
      await handleGetDashboards();
      onSelectDashboard(dashboardId);
      return dashboardId;
    } catch (err: any) {
      message.error(
        `创建仪表盘失败: ${err?.errors?.[0]?.message || '未知错误'}`
      );
    }
  };

  const onSelectDashboard = (id: number) => {
    console.log("选中仪表盘", id);
    
    setActiveView('dashboard');
    setSelectedDashboard(id);
    setSelectedCollection(''); // 取消工作表选中
  };

  const onOperateDashboard = (
    e: React.MouseEvent<HTMLElement>,
    dashboard: DashboardInfo
  ) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    const target = e.currentTarget as HTMLElement;
    const parent = target.offsetParent as HTMLElement;
    const parentRect = parent.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    setFocusDashboard(dashboard.id);
    setFocusDashboardInfo(dashboard);
    setDashboardCardVisible(true);
    setSheetCardVisible(false);
    setRenameDashboard(false);
    const left = rect.left - parentRect.left + parent.scrollLeft;
    const top = rect.bottom - parentRect.top + parent.scrollTop;
    const adjustedPosition = calculateCardPosition(left, top, 'sider');
    setCardPosition(adjustedPosition);
  };

  const onRenameDashboardConfirm = async (
    e:
      | React.FocusEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>,
    dashboard: DashboardInfo
  ) => {
    if (!api || !dashboard) return;
    const newName = (e.target as HTMLInputElement).value.trim();
    if (!newName || newName === dashboard.name) {
      setRenameDashboard(false);
      return;
    }
    try {
      await api.updateDashboard(dashboard.id, {name: newName});
      message.success('重命名成功');
      await handleGetDashboards();
    } catch (err: any) {
      message.error(`重命名失败: ${err?.errors?.[0]?.message}`);
    } finally {
      setRenameDashboard(false);
    }
  };

  const onDeleteDashboard = async (dashboardId: number) => {
    if (!api) return;
    try {
      await api.deleteDashboard(dashboardId);
      message.success('删除成功');
      await handleGetDashboards();
      if (selectedDashboard === dashboardId) {
        setSelectedDashboard(null);
        setActiveView('dashboard'); // 或者切换到某个默认视图
      }
    } catch (err: any) {
      message.error(`删除失败: ${err?.errors?.[0]?.message}`);
    } finally {
      setDashboardCardVisible(false);
    }
  };

  const onSetDashboardPermissions = async (permission: string) => {
    if (!api || !focusDashboardInfo?.permissionId) return;
    try {
      setDashboardCardVisible(false);
      await api.updateDashboardPermission(focusDashboardInfo.permissionId, {
        permission_code: permission
      });
      await handleGetDashboards();
      message.success('权限设置成功');
    } catch (err: any) {
      message.error(`权限设置失败: ${err?.errors?.[0]?.message}`);
    }
  };

  // --- AI生成仪表盘 ---
  // 处理从AI弹窗采纳的图表
  const handleAddChartsToDashboard = async (charts: ChartConfig[]) => {
    if (!api) {
      message.error('API 未初始化');
      return;
    }
    const {currentSheet} = stateRef.current;
    if (!currentSheet) {
      message.error('无法确定当前工作表，无法添加图表');
      return;
    }

    setLoading(true);
    try {
      // 1. 查找是否已存在为当前工作表生成的AI仪表盘
      let targetDashboard = allDashboardList.find(
        d => d.generatedBy === currentSheet.collection
      );
      let targetDashboardId = targetDashboard?.id || null;

      // 2. 如果不存在，则创建新的AI仪表盘
      if (!targetDashboardId) {
        message.info('未找到关联的AI仪表盘，正在为您创建一个...');
        const newId = await onCreateDashboard('ai');
        if (newId) {
          targetDashboardId = newId;
        } else {
          message.error('创建AI仪表盘失败，无法添加图表');
          return; // 创建失败则中止
        }
      }

      if (!targetDashboardId) {
        message.error('无法确定目标仪表盘，请重试');
        return;
      }

      // 3. 切换到目标仪表盘视图
      onSelectDashboard(targetDashboardId);

      // 4. 触发事件，让DashboardView组件来处理图表的添加
      // 使用setTimeout确保DashboardView已经切换并准备好接收事件
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('add-ai-charts', {detail: charts})
        );
      }, 300);
    } catch (error: any) {
      message.error(`处理AI图表失败: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 应用筛选条件
  const handleApplyFilter = useCallback(
    async (filters: FilterRule[], logicRelation: 'and' | 'or' = 'and') => {
      if (!tableInstance.current) return;

      console.log('应用筛选条件:', filters, '逻辑关系:', logicRelation); // 添加调试日志

      setAppliedFilters(filters);
      setFilterLogicRelation(logicRelation);

      if (filters.length === 0) {
        // 没有筛选条件，显示所有数据
        tableInstance.current.setRecords(recordsRef.current);
        return;
      }

      // 应用筛选逻辑
      const filteredRecords = recordsRef.current.filter(record => {
        if (logicRelation === 'or') {
          // 任意条件满足即可（OR 逻辑）
          return filters.some(filter => {
            return applyFilterCondition(
              record[filter.field],
              filter.operator,
              filter.value
            );
          });
        } else {
          // 所有条件都必须满足（AND 逻辑）
          return filters.every(filter => {
            return applyFilterCondition(
              record[filter.field],
              filter.operator,
              filter.value
            );
          });
        }
      });

      tableInstance.current.setRecords(filteredRecords);
    },
    []
  );

  // 筛选条件判断逻辑
  const applyFilterCondition = (
    fieldValue: any,
    operator: string,
    filterValue: any
  ): boolean => {
    // 处理空值情况
    const isEmpty =
      fieldValue === null || fieldValue === undefined || fieldValue === '';

    switch (operator) {
      case 'is_empty':
        return isEmpty;
      case 'is_not_empty':
        return !isEmpty;
      case 'equals':
        return String(fieldValue) === String(filterValue);
      case 'not_equals':
        return String(fieldValue) !== String(filterValue);
      case 'contains':
        return String(fieldValue)
          .toLowerCase()
          .includes(String(filterValue).toLowerCase());
      case 'not_contains':
        return !String(fieldValue)
          .toLowerCase()
          .includes(String(filterValue).toLowerCase());
      case 'starts_with':
        return String(fieldValue)
          .toLowerCase()
          .startsWith(String(filterValue).toLowerCase());
      case 'ends_with':
        return String(fieldValue)
          .toLowerCase()
          .endsWith(String(filterValue).toLowerCase());
      case 'greater_than':
        return Number(fieldValue) > Number(filterValue);
      case 'greater_than_or_equal':
        return Number(fieldValue) >= Number(filterValue);
      case 'less_than':
        return Number(fieldValue) < Number(filterValue);
      case 'less_than_or_equal':
        return Number(fieldValue) <= Number(filterValue);
      default:
        return true;
    }
  };

  // --- 视图管理函数 ---

  const getCurrentCollectionViewStateSync = useCallback(() => {
    if (!selectedCollection) {
      return {
        views: [
          {
            id: 'default',
            name: '全部数据',
            filters: [],
            logicRelation: 'and' as const,
            isDefault: true
          }
        ],
        activeViewId: 'default',
        viewFiltersState: {
          default: {filters: [], logicRelation: 'and' as const}
        }
      };
    }

    // 直接从最新的状态获取，避免闭包问题
    const cachedState = collectionViews[selectedCollection];
    if (cachedState) {
      return cachedState;
    }

    // 返回默认状态
    const defaultState = {
      views: [
        {
          id: 'default',
          name: '全部数据',
          filters: [],
          logicRelation: 'and' as const,
          isDefault: true
        }
      ],
      activeViewId: 'default',
      viewFiltersState: {
        default: {filters: [], logicRelation: 'and' as const}
      }
    };

    // 同时缓存默认状态，避免重复计算
    setCollectionViews(prev => ({
      ...prev,
      [selectedCollection]: defaultState
    }));

    return defaultState;
  }, [selectedCollection, collectionViews]);

  // 更新当前collection的视图状态
  const updateCurrentCollectionViewState = useCallback(
    (
      newState: Partial<{
        views: ViewConfig[];
        activeViewId: string;
        viewFiltersState: Record<
          string,
          {filters: FilterRule[]; logicRelation: 'and' | 'or'}
        >;
      }>
    ) => {
      if (!selectedCollection) return;

      setCollectionViews(prev => {
        const currentState = prev[selectedCollection] || {
          views: [
            {
              id: 'default',
              name: '全部数据',
              filters: [],
              logicRelation: 'and' as const,
              isDefault: true
            }
          ],
          activeViewId: 'default',
          viewFiltersState: {default: {filters: [], logicRelation: 'and'}}
        };

        return {
          ...prev,
          [selectedCollection]: {
            ...currentState,
            ...newState
          }
        };
      });
    },
    [selectedCollection]
  );

  // 使用 useMemo 缓存当前状态，避免重复计算
  const currentCollectionState = useMemo(() => {
    const state = getCurrentCollectionViewStateSync();

    // 确保activeViewId在views中存在
    const validActiveViewId = state.views.find(v => v.id === state.activeViewId)
      ? state.activeViewId
      : 'default';

    if (validActiveViewId !== state.activeViewId) {
      // 如果activeViewId无效，使用默认值并更新状态
      const correctedState = {
        ...state,
        activeViewId: validActiveViewId
      };

      // 异步更新状态
      setTimeout(() => {
        updateCurrentCollectionViewState({activeViewId: validActiveViewId});
      }, 0);

      return correctedState;
    }

    console.log('当前集合状态:', {
      selectedCollection,
      activeViewId: state.activeViewId,
      viewsCount: state.views.length,
      views: state.views.map(v => ({id: v.id, name: v.name}))
    });

    return state;
  }, [
    getCurrentCollectionViewStateSync,
    selectedCollection,
    updateCurrentCollectionViewState
  ]);

  // 创建默认视图的函数
  const createDefaultView = async (collection: string) => {
    if (!api || !userId) return;

    try {
      await api.createPreset({
        collection,
        bookmark: 'default',
        layout: '全部数据',
        filter: [],
        user: userId
      });
      console.log('默认视图创建成功');
    } catch (error) {
      console.error('创建默认视图失败:', error);
    }
  };

  // 视图管理函数
  const handleViewChange = useCallback(
    async (viewId: string) => {
      console.log('切换到视图:', viewId);

      try {
        const collectionState = getCurrentCollectionViewStateSync();
        const view = collectionState.views.find(v => v.id === viewId);

        if (!view) {
          console.error('未找到视图:', viewId);
          return;
        }

        // 批量更新状态
        unstable_batchedUpdates(() => {
          // 更新活动视图ID
          updateCurrentCollectionViewState({activeViewId: viewId});

          // 恢复该视图保存的筛选状态（而不是临时状态）
          const savedViewFiltersState = {
            ...collectionState.viewFiltersState,
            [viewId]: {
              filters: [...view.filters], // 使用视图保存的筛选条件
              logicRelation: view.logicRelation // 使用视图保存的逻辑关系
            }
          };

          updateCurrentCollectionViewState({
            viewFiltersState: savedViewFiltersState
          });
        });

        // 应用视图保存的筛选条件
        await handleApplyFilter(view.filters, view.logicRelation);

        console.log(
          '视图切换完成:',
          viewId,
          '已恢复保存的筛选条件:',
          view.filters.length
        );
      } catch (error) {
        console.error('视图切换失败:', error);
      }
    },
    [
      getCurrentCollectionViewStateSync,
      updateCurrentCollectionViewState,
      handleApplyFilter
    ]
  );

  const handleViewCreate = async (
    name: string,
    filters: FilterRule[],
    logicRelation: 'and' | 'or'
  ) => {
    if (!api || !userId || !selectedCollection) return;

    try {
      const viewId = `view_${Date.now()}`;

      // 转换筛选条件格式以符合后端要求
      const backendFilters = filters.map((filter, index) => ({
        key: `filter_${index}`,
        field: filter.field,
        operator: filter.operator,
        value: filter.value
      }));

      // 调用后端API创建视图预设
      await api.createPreset({
        collection: selectedCollection,
        bookmark: viewId,
        layout: name,
        filter: backendFilters,
        user: userId
      });

      // 批量更新本地状态
      unstable_batchedUpdates(() => {
        const collectionState = getCurrentCollectionViewStateSync();
        const newView = {
          id: viewId,
          name,
          filters,
          logicRelation,
          isDefault: false
        };

        const updatedViews = [...collectionState.views, newView];
        const updatedViewFiltersState = {
          ...collectionState.viewFiltersState,
          [viewId]: {filters, logicRelation}
        };

        updateCurrentCollectionViewState({
          views: updatedViews,
          activeViewId: viewId,
          viewFiltersState: updatedViewFiltersState
        });
      });

      // 应用新视图的筛选条件
      await handleApplyFilter(filters, logicRelation);

      message.success('视图创建成功');
    } catch (error: any) {
      message.error(
        `创建视图失败: ${error?.errors?.[0]?.message || '未知错误'}`
      );
    }
  };

  const handleViewUpdate = async (
    viewId: string,
    name: string,
    filters: FilterRule[],
    logicRelation: 'and' | 'or',
    silent: boolean = false
  ) => {
    if (!api || !userId || !selectedCollection) return;

    try {
      console.log(`更新视图配置 [${viewId}]:`, {
        name,
        filters: filters.length,
        logicRelation,
        silent
      });

      // 转换筛选条件格式
      const backendFilters = filters.map((filter, index) => ({
        key: `filter_${index}`,
        field: filter.field,
        operator: filter.operator,
        value: filter.value
      }));

      // 查找对应的预设ID
      const presets = await api.getPresetsDetail({
        'filter[collection]': selectedCollection,
        'filter[user]': userId,
        'filter[bookmark]': viewId
      });

      if (presets.length > 0) {
        const presetId = presets[0].id;

        // 更新预设
        await api.updatePreset(presetId, {
          layout: name,
          filter: backendFilters
        });

        // 批量更新本地状态
        unstable_batchedUpdates(() => {
          const collectionState = getCurrentCollectionViewStateSync();

          // 更新views中的筛选配置
          const updatedViews = collectionState.views.map(view =>
            view.id === viewId ? {...view, name, filters, logicRelation} : view
          );

          // 同步更新viewFiltersState，确保临时状态与保存状态一致
          const updatedViewFiltersState = {
            ...collectionState.viewFiltersState,
            [viewId]: {filters, logicRelation}
          };

          updateCurrentCollectionViewState({
            views: updatedViews,
            viewFiltersState: updatedViewFiltersState
          });
        });

        if (!silent) {
          message.success('视图更新成功');
        }

        console.log(`视图配置保存完成 [${viewId}]`);
      }
    } catch (error: any) {
      console.error('更新视图失败:', error);
      if (!silent) {
        message.error(
          `更新视图失败: ${error?.errors?.[0]?.message || '未知错误'}`
        );
      }
    }
  };

  const handleViewDelete = async (viewId: string) => {
    if (!api || !userId || !selectedCollection || viewId === 'default') return;

    try {
      // 查找对应的预设ID
      const presets = await api.getPresetsDetail({
        'filter[collection]': selectedCollection,
        'filter[user]': userId,
        'filter[bookmark]': viewId
      });

      if (presets.length > 0) {
        const presetId = presets[0].id;

        // 删除预设
        await api.deletePreset(presetId);

        // 重新获取视图列表
        await refreshViewsFromBackend();

        // 如果删除的是当前激活的视图，切换到默认视图
        const collectionState = getCurrentCollectionViewStateSync();
        if (viewId === collectionState.activeViewId) {
          updateCurrentCollectionViewState({activeViewId: 'default'});
          const defaultView = collectionState.views.find(
            v => v.id === 'default'
          );
          if (defaultView) {
            await handleApplyFilter(
              defaultView.filters,
              defaultView.logicRelation
            );
          }
        }

        message.success('视图删除成功');
      }
    } catch (error: any) {
      message.error(
        `删除视图失败: ${error?.errors?.[0]?.message || '未知错误'}`
      );
    }
  };

  const handleViewRename = async (viewId: string, newName: string) => {
    if (!api || !userId || !selectedCollection || viewId === 'default') return;

    try {
      // 查找对应的预设ID
      const presets = await api.getPresetsDetail({
        'filter[collection]': selectedCollection,
        'filter[user]': userId,
        'filter[bookmark]': viewId
      });

      if (presets.length > 0) {
        const presetId = presets[0].id;

        // 更新预设名称
        await api.updatePreset(presetId, {
          layout: newName
        });

        // 重新获取视图列表
        await refreshViewsFromBackend();

        message.success('视图重命名成功');
      }
    } catch (error: any) {
      message.error(
        `重命名视图失败: ${error?.errors?.[0]?.message || '未知错误'}`
      );
    }
  };

  const getCurrentViewFilters = useCallback(() => {
    const collectionState = getCurrentCollectionViewStateSync();
    const activeViewId = collectionState.activeViewId;

    // 优先从viewFiltersState获取最新的临时筛选状态
    const currentViewState = collectionState.viewFiltersState[activeViewId];

    if (currentViewState) {
      return currentViewState;
    }

    // 如果viewFiltersState中没有，则从views配置中获取保存的状态
    const currentView = collectionState.views.find(v => v.id === activeViewId);
    if (currentView) {
      // 初始化viewFiltersState，避免下次查询失败
      const newViewFiltersState = {
        ...collectionState.viewFiltersState,
        [activeViewId]: {
          filters: currentView.filters,
          logicRelation: currentView.logicRelation
        }
      };

      // 异步更新，避免在render期间更新状态
      setTimeout(() => {
        updateCurrentCollectionViewState({
          viewFiltersState: newViewFiltersState
        });
      }, 0);

      return {
        filters: currentView.filters,
        logicRelation: currentView.logicRelation
      };
    }

    // 最终回退到默认值
    return {filters: [] as FilterRule[], logicRelation: 'and' as const};
  }, [getCurrentCollectionViewStateSync, updateCurrentCollectionViewState]);

  // 新增函数：手动保存当前筛选条件到视图
  const handleSaveCurrentFiltersToView = useCallback(async () => {
    const collectionState = getCurrentCollectionViewStateSync();
    const currentViewFilters = getCurrentViewFilters();
    const activeView = collectionState.views.find(
      v => v.id === collectionState.activeViewId
    );

    if (!activeView) {
      console.error('未找到当前活动视图');
      return;
    }

    if (activeView.isDefault) {
      console.error('默认视图不支持保存筛选条件');
      return;
    }

    try {
      console.log('手动保存筛选条件到视图:', activeView.name);

      // 保存筛选条件到后端，并启用持久化
      await handleViewUpdate(
        activeView.id,
        activeView.name,
        currentViewFilters.filters,
        currentViewFilters.logicRelation,
        false // 非静默模式，显示成功消息
      );
    } catch (error) {
      console.error('保存筛选条件失败:', error);
    }
  }, [
    getCurrentCollectionViewStateSync,
    getCurrentViewFilters,
    handleViewUpdate
  ]);

  // 从后端刷新视图列表
  const refreshViewsFromBackend = useCallback(async () => {
    if (!selectedCollection || !api) {
      console.log('跳过视图刷新: 缺少必要参数');
      return;
    }

    try {
      console.log('开始刷新视图列表:', selectedCollection);
      const viewState = await getCurrentCollectionViewState();

      // 确保activeViewId有效
      const validActiveViewId = viewState.views.find(
        v => v.id === viewState.activeViewId
      )
        ? viewState.activeViewId
        : 'default';

      const correctedViewState = {
        ...viewState,
        activeViewId: validActiveViewId
      };

      // 批量更新状态
      unstable_batchedUpdates(() => {
        updateCurrentCollectionViewState(correctedViewState);
      });

      console.log(
        '视图刷新完成:',
        selectedCollection,
        '当前视图:',
        validActiveViewId
      );
    } catch (error) {
      console.error('刷新视图列表失败:', error);
      // 设置默认状态
      const defaultState = {
        views: [
          {
            id: 'default',
            name: '全部数据',
            filters: [],
            logicRelation: 'and' as const,
            isDefault: true
          }
        ],
        activeViewId: 'default',
        viewFiltersState: {
          default: {filters: [], logicRelation: 'and' as const}
        }
      };
      updateCurrentCollectionViewState(defaultState);
    }
  }, [
    selectedCollection,
    api,
    getCurrentCollectionViewState,
    updateCurrentCollectionViewState
  ]);

  // 更新当前视图的筛选状态
  const updateCurrentViewFilters = useCallback(
    (
      filters: FilterRule[],
      logicRelation: 'and' | 'or',
      persistToBackend: boolean = false // 新增参数，控制是否持久化到后端
    ) => {
      const collectionState = getCurrentCollectionViewStateSync();
      const activeViewId = collectionState.activeViewId;

      // 更新本地筛选状态
      const newViewFiltersState = {
        ...collectionState.viewFiltersState,
        [activeViewId]: {filters, logicRelation}
      };

      updateCurrentCollectionViewState({
        viewFiltersState: newViewFiltersState
      });

      // 如果需要持久化到后端
      if (persistToBackend && activeViewId !== 'default') {
        const currentView = collectionState.views.find(
          v => v.id === activeViewId
        );
        if (currentView) {
          // 异步保存，不阻塞UI
          setTimeout(() => {
            handleViewUpdate(
              currentView.id,
              currentView.name,
              filters,
              logicRelation,
              true
            );
          }, 100);
        }
      }
    },
    [
      getCurrentCollectionViewStateSync,
      updateCurrentCollectionViewState,
      handleViewUpdate
    ]
  );

  // 修改筛选应用函数
  const handleApplyFilterWithView = useCallback(
    async (filters: FilterRule[], logicRelation: 'and' | 'or' = 'and') => {
      console.log('应用筛选条件到视图:', filters, logicRelation);

      // 1. 更新当前视图的临时筛选状态（不持久化到后端）
      updateCurrentViewFilters(filters, logicRelation, false);

      // 2. 应用筛选逻辑到表格
      await handleApplyFilter(filters, logicRelation);

      console.log('筛选条件已应用到表格，等待用户手动保存');
    },
    [handleApplyFilter, updateCurrentViewFilters]
  );

  // --- Effects ---
  // 每次渲染时都更新 ref 的值
  useEffect(() => {
    stateRef.current = {
      allSheetList,
      currentSheet,
      currentSheetPermissions,
      selectedCollection,
      focusCollection,
      focusSheet,
      focusSheetPermissions,
      focusSheetPermissionIds,
      direction,
      currentCol,
      currentRow,
      userId,
      selectedDashboard,
      focusDashboard,
      isRenameDashboard
    };
  });

  useEffect(() => {
    if (api && tableReady) {
      handleGetSheets();
      handleGetDashboards();
    }
  }, [api, tableReady]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const cardNode = sheetCardRef.current;
      const renameNode = renameInputRef.current;
      const clickedOpBtn = (e.target as HTMLElement).closest('.operation-btn');

      // 添加对权限相关元素的检查
      const permissionButton = (e.target as HTMLElement).closest(
        '[data-permission-button]'
      );
      const permissionMenu = (e.target as HTMLElement).closest(
        '[data-permission-menu]'
      );
      const permissionDetail = (e.target as HTMLElement).closest(
        '[data-permission-detail]'
      );

      if (
        (cardNode && cardNode.contains(e.target as Node)) ||
        clickedOpBtn ||
        (renameNode && renameNode.contains(e.target as Node)) ||
        permissionButton ||
        permissionMenu ||
        permissionDetail
      ) {
        return;
      }

      if (
        (cardNode && cardNode.contains(e.target as Node)) ||
        clickedOpBtn ||
        (renameNode && renameNode.contains(e.target as Node))
      ) {
        return;
      }
      setSheetCardVisible(false);
      setFocusCollection('');
      setRenameSheet(false);
      setDashboardCardVisible(false);
      setFocusDashboard(null);
      setRenameDashboard(false);
    };
    document.addEventListener('click', handleClickOutside, true);
    return () =>
      document.removeEventListener('click', handleClickOutside, true);
  }, []);

  // 在组件内部注册自定义编辑器（需要 props 的）
  useEffect(() => {
    if (!api || !props.apiUrl || !props.adminToken) return;
    console.log('注册自定义编辑器，使用 apiUrl:', props, props.apiUrl);

    // 创建图片编辑器实例，现在可以访问 props
    const imageEditor = new ImageEditor({
      api: api,
      apiUrl: props.apiUrl, // 现在可以正确访问 props
      token: props.adminToken,
      maxSize: 10, // 10MB
      multiple: false,
      onUpload: async (file: File) => {
        try {
          console.log('开始上传图片文件:', file.name, file.size);

          // 创建FormData对象
          const formData = new FormData();
          formData.append('file', file);

          // 调用API上传文件
          const result = await api.createFile(formData);

          console.log('图片上传成功，文件ID:', result.id);

          // 返回文件ID而不是base64
          return result.id;
        } catch (error) {
          console.error('图片上传失败:', error);

          // 上传失败时，降级到base64方案
          console.log('降级到base64方案');
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        }
      }
    });

    // 创建文件编辑器实例
    const fileEditor = new FileEditor({
      maxSize: 50, // 50MB
      multiple: true,
      onUpload: async (file: File) => {
        try {
          console.log('开始上传文件:', file.name, file.size);

          // 创建FormData对象
          const formData = new FormData();
          formData.append('file', file);
          formData.append('folder', 'files'); // 可选：指定存储文件夹

          // 调用API上传文件
          const result = await api.createFile(formData);

          console.log('文件上传成功，文件ID:', result.id);

          // 返回文件信息，包含ID
          return {
            id: result.id, // 文件ID，用于数据库存储
            url: `${props.apiUrl}/assets/${result.id}`, // 用于下载的URL
            name: file.name,
            size: file.size
          };
        } catch (error) {
          console.error('文件上传失败:', error);

          // 上传失败时，降级到blob URL方案
          console.log('降级到blob URL方案');
          return {
            id: null,
            url: URL.createObjectURL(file),
            name: file.name,
            size: file.size
          };
        }
      }
    });

    // 注册自定义编辑器
    VTable.register.editor('image-editor', imageEditor);
    VTable.register.editor('file-editor', fileEditor);

    console.log('自定义编辑器注册完成3333');
  }, [props.apiUrl, props.adminToken]);

  // 初始化表格实例配置
  useEffect(() => {
    if (containerRef.current && !tableInstance.current) {
      optionRef.current = {
        records: [],
        columns: [],
        // widthMode: 'standard' as const,
        // theme: VTable.themes.ARCO, //DARK
        theme: {
          underlayBackgroundColor: 'transparent',
          defaultStyle:{
            fontSize: 14,
          },
          headerStyle: {
            bgColor: '#EEF1F5',
            borderColor: '#EEF1F5',
            // borderLineWidth: 1,
          },
          bodyStyle: {
            bgColor: '#ffffff',
            borderColor: '#f3f4f6',
            hover: {
              cellBgColor: '#f9fafb',
            },
            select: {
              cellBgColor: '#ffffff',
            },
          },
          frameStyle: {
            // borderColor: '#e4e6eb',
            // borderLineWidth: 2,
            cornerRadius: 10,
          },
          scrollStyle: {
            scrollSliderColor: '#d1d5db',
          },
          selectionStyle: {
            cellBorderLineWidth: 1,
            // cellBorderColor: '#F8F9FC',
            // cellBgColor: '#F8F9FC'
          },
        },
        rowSeriesNumber: {
          title: '序号',
          dragOrder: true,
          width: 100
        },
        keyboardOptions: {
          copySelected: true,
          pasteValueToCell: true,
          selectAllOnCtrlA: true
        },
        hover: {highlightMode: 'row' as const, disableHeaderHover: true},
        menu: {
          renderMode: 'html' as const,
          contextMenuItems: (field: string, row: number, col: number) => {
            const selectCells =
              tableInstance.current?.getSelectedCellInfos() || [];
            // 单列选择 (点击列头且只选中一列)
            if (
              row === 0 &&
              col > 0 &&
              selectCells?.[0]?.length === 1 &&
              field !== 'addColumn'
            ) {
              const menuItems = [];
              menuItems.push(
                {
                  text: '向左插入列',
                  menuKey: 'insertColLeft',
                  icon: {
                    type: 'svg',
                    svg: svgIconMap['leftArrow'],
                    width: 20,
                    height: 20,
                    name: 'insertColLeft',
                    positionType: VTable.TYPES.IconPosition.contentLeft,
                    visibleTime: 'always'
                  }
                },
                {
                  text: '向右插入列',
                  menuKey: 'insertColRight',
                  icon: {
                    type: 'svg',
                    svg: svgIconMap['rightArrow'],
                    width: 20,
                    height: 20,
                    name: 'insertColRight',
                    positionType: VTable.TYPES.IconPosition.contentLeft,
                    visibleTime: 'always'
                  }
                }
              );
              menuItems.push({
                text: '<div style="color: #d81e06;">删除列</div>',
                menuKey: 'deleteCol',
                icon: {
                  type: 'svg',
                  svg: svgIconMap['delete'],
                  width: 20,
                  height: 20,
                  name: 'deleteCol',
                  positionType: VTable.TYPES.IconPosition.contentLeft,
                  visibleTime: 'always'
                }
              });
              return menuItems;
            }

            // 普通单元格选择且只选中一行
            else if (
              selectCells?.length === 1 &&
              selectCells[0]?.[0]?.row > 0
            ) {
              const menuItems = [];
              menuItems.push(
                {
                  text: '向上插入行',
                  menuKey: 'insertRowUp',
                  icon: {
                    type: 'svg',
                    svg: svgIconMap['upArrow'],
                    width: 20,
                    height: 20,
                    name: 'insertRowUp',
                    positionType: VTable.TYPES.IconPosition.contentLeft,
                    visibleTime: 'always'
                  }
                },
                {
                  text: '向下插入行',
                  menuKey: 'insertRowDown',
                  icon: {
                    type: 'svg',
                    svg: svgIconMap['downArrow'],
                    width: 20,
                    height: 20,
                    name: 'insertRowDown',
                    positionType: VTable.TYPES.IconPosition.contentLeft,
                    visibleTime: 'always'
                  }
                }
              );
              menuItems.push({
                text: '<span style="color: #d81e06;">删除行</span>',
                menuKey: 'deleteRow',
                icon: {
                  type: 'svg',
                  svg: svgIconMap['delete'],
                  width: 20,
                  height: 20,
                  name: 'deleteRow',
                  positionType: VTable.TYPES.IconPosition.contentLeft,
                  visibleTime: 'always'
                }
              });
              return menuItems;
            }

            // 多列选择 (选择了多列)
            else if (row === 0 && col > 0 && selectCells?.[0]?.length > 1) {
              return [
                {
                  text: '<div style="color: #d81e06;">删除选中列</div>',
                  menuKey: 'deleteCol',
                  icon: {
                    type: 'svg',
                    svg: svgIconMap['delete'],
                    width: 20,
                    height: 20,
                    name: 'deleteCol',
                    positionType: VTable.TYPES.IconPosition.contentLeft,
                    visibleTime: 'always'
                  }
                }
              ];
              return [];
            }
            // 多行选择 (选择了多行)
            else if (
              row > 0 &&
              selectCells?.length > 1 &&
              selectCells[0]?.[0]?.row > 0
            ) {
              const menuItems = [];
              if (selectCells?.length > 0) {
                menuItems.push({
                  text: '<span style="color: #d81e06;">删除选中行</span>',
                  menuKey: 'deleteRow',
                  icon: {
                    type: 'svg',
                    svg: svgIconMap['delete'],
                    width: 20,
                    height: 20,
                    name: 'deleteRow',
                    positionType: VTable.TYPES.IconPosition.contentLeft,
                    visibleTime: 'always'
                  }
                });
              }
              return menuItems;
            } else {
              return [];
            }
          }
        }
      };

      tableInstance.current = new VTable.ListTable(
        containerRef.current,
        optionRef as any
      );
      // 点击单元格事件 v1
      tableInstance.current.on(CLICK_CELL, async args => {
        // 添加列图标
        if (
          args.field === 'addColumn' &&
          args.cellLocation === 'columnHeader'
        ) {
          if (!hasPermission()) {
            message.error('没有权限进行此操作');
            return;
          }

          if (args.event instanceof PointerEvent) {
            const left = args.event.clientX;
            const top = args.event.clientY;
            const adjustedPosition = calculateCardPosition(
              left,
              top,
              'content'
            );
            setCardPosition(adjustedPosition);
            setDirection('left');
            setOperateType('add');
            setEditField('');
            setSelectedFieldType('text');
            setCardVisible(true);
            setCurrentRow(args.row);
            setCurrentCol(args.col);
          }
        }
      });

      // 右键菜单事件
      tableInstance.current.on(CONTEXTMENU_CELL, args => {
        setCurrentRow(args.row);
        setCurrentCol(args.col);
      });

      tableInstance.current.on(DROPDOWN_MENU_CLICK, async args => {
        const {menuKey, field, row, col} = args;

        switch (menuKey) {
          case 'insertRowUp':
            setDirection('up');
            setCurrentRow(row);
            await onAddRow('notLastRow');
            break;

          case 'insertRowDown':
            setDirection('down');
            setCurrentRow(row);
            await onAddRow('notLastRow');
            break;

          case 'deleteRow':
            await onDeleteRows();
            break;

          case 'insertColLeft':
            // 向左插入列的逻辑
            if (field && field !== 'addColumn') {
              if (!hasPermission()) {
                message.error('没有权限进行此操作');
                return;
              }
              setDirection('left');
              setOperateType('add');
              setEditField('');
              setSelectedFieldType('text');
              setCurrentRow(args.row);
              setCurrentCol(args.col);
              if (args.event instanceof PointerEvent) {
                const left = args.event.clientX;
                const top = args.event.clientY;
                const adjustedPosition = calculateCardPosition(
                  left,
                  top,
                  'content'
                );
                setCardPosition(adjustedPosition);
                setCardVisible(true);
              }
            }
            break;

          case 'insertColRight':
            if (!hasPermission()) {
              message.error('没有权限进行此操作');
              return;
            }
            // 向右插入列的逻辑
            if (field && field !== 'addColumn') {
              setDirection('right');
              setOperateType('add');
              setEditField('');
              setSelectedFieldType('text');
              setCurrentRow(args.row);
              setCurrentCol(args.col);
              if (args.event instanceof PointerEvent) {
                const left = args.event.clientX;
                const top = args.event.clientY;
                const adjustedPosition = calculateCardPosition(
                  left,
                  top,
                  'content'
                );
                setCardPosition(adjustedPosition);
                setCardVisible(true);
              }
            }
            break;

          case 'deleteCol':
            // 删除列的逻辑
            const selectedFields = selectCellsRef.current[0].map(
              (cell: any) => cell.field
            );
            for (const fieldName of selectedFields) {
              await onDeleteField(fieldName);
            }
            break;

          case 'deleteField':
            if (field && field !== 'addColumn') {
              const fieldName =
                typeof field === 'string' ? field : String(field);
              await onDeleteField(fieldName);
            }
            break;

          default:
            console.log('未处理的菜单项:', menuKey);
            break;
        }
      });

      tableInstance.current.on(CHANGE_CELL_VALUE, async args => {
        const {row: rowIndex, col: columnIndex, changedValue} = args;
        if (!api) return;
        console.log('删除图片或文件时的值变化:', changedValue);

        // 检查是否有实际的值变化
        if (
          args.changedValue === undefined ||
          args.changedValue === null ||
          (args.changedValue === '' && args.currentValue === null)
        )
          return;

        const normalizedValue = changedValue === '' ? null : changedValue;

        const isListEditor =
          typeof columnsRef.current[columnIndex - 1].editor === 'object'; // 其他类型是字符串
        const recordId = recordsRef.current[rowIndex - 1]?.id;
        const field = columnsRef.current[columnIndex - 1].field as string;
        if (recordId) {
          try {
            await api.updateRecord(selectedCollectionRef.current, recordId, {
              [field]: isListEditor ? [normalizedValue] : normalizedValue
            });
          } catch (err: any) {
            if (err?.errors?.[0]?.message === 'An unexpected error occurred.') {
              message.error('只允许输入数字');
            } else if (
              err?.errors?.[0]?.message.includes(
                "You don't have permission to access collection"
              )
            ) {
              message.error({content: `更新失败：无编辑权限`});
            } else {
              message.error(`更新数据失败: ${err?.errors?.[0]?.message}`);
            }
          }
          await updateTableRows(selectedCollectionRef.current);
        }
      });

      // 拖拽表头或行头事件
      tableInstance.current.on(CHANGE_HEADER_POSITION, async args => {
        if (!api) return;

        // 拖拽行头，更新行顺序
        if (args.source.col === 0 && args.target.col === 0) {
          if (!hasPermission()) {
            message.error('没有权限进行此操作');

            // 使用快照恢复表格的原始顺序
            tableInstance.current?.setRecords(recordsSnapshotRef.current);
            // 同样用快照更新 recordsRef，保持其与UI同步
            recordsRef.current = recordsSnapshotRef.current;
            return;
          }
          try {
            const fromId = recordsSnapshotRef.current[args.source.row - 1]?.id;
            const toId = recordsSnapshotRef.current[args.target.row - 1]?.id;

            if (fromId && toId) {
              await api.sortRecord(selectedCollectionRef.current, fromId, toId);
              // 拖拽后立即刷新数据，以获取后端排序后的最新顺序
              await updateTableRows(selectedCollectionRef.current);
            }
          } catch (err: any) {
            message.error(`行排序失败: ${err?.errors?.[0]?.message}`);
            // 排序失败时，将表格还原，避免UI与后端不一致
            tableInstance.current?.setRecords(recordsSnapshotRef.current);
            recordsRef.current = recordsSnapshotRef.current;
          }
        }
      });

      // 双击表头事件，用于编辑字段
      tableInstance.current.on(DBLCLICK_CELL, async args => {
        const {field, title, cellLocation, event} = args;
        if (cellLocation === 'columnHeader' && field && field !== 'addColumn') {
          if (!hasPermission()) {
            // 没有权限进行此操作
            return;
          }
          const editTitle = String(title);
          const fieldName = String(field);
          const column = columnsRef.current.find(col => col.field === field);
          if (column) {
            setOperateType('edit');
            setEditField(editTitle);
            setPostFieldName(fieldName);
            let editorType = 'text';
            if (typeof column.editor === 'string') {
              editorType = column.editor.replace('-editor', '');
            } else if (column.editor && typeof column.editor === 'object') {
              editorType = 'list';
            }
            setSelectedFieldType(editorType);

            if (event instanceof PointerEvent) {
              const left = event.x;
              const top = event.y;
              const adjustedPosition = calculateCardPosition(
                left,
                top,
                'content'
              );
              setCardPosition(adjustedPosition);
              setCardVisible(true);
            }
          }
        }
      });

      // 鼠标在表格区域按下触发该事件
      tableInstance.current.on(MOUSEDOWN_TABLE, args => {
        setCardVisible(false);

        // 在拖拽开始前，创建数据快照
        if (args.event instanceof PointerEvent && args.event.clientX < 350) {
          recordsSnapshotRef.current = [...recordsRef.current];
        }
      });

      // 鼠标松开事件
      tableInstance.current.on(MOUSEUP_CELL, () => {
        selectCellsRef.current =
          tableInstance.current?.getSelectedCellInfos() ?? [];
      });

      // 复制事件
      tableInstance.current.on(COPY_DATA, args => {
        console.log('复制数据事件', args);
        if (args.copyData) {
          const dataLength =
            args.cellRange[0].end.row - args.cellRange[0].start.row + 1;
          message.success({content: `已复制${dataLength}行数据`});
        }

        // const selectCells = tableInstance.current?.getSelectedCellInfos() || [];
        // if (selectCells.length > 0) {
        //   const copiedData = selectCells.map(row =>
        //     row.map(cell => cell.value)
        //   );
        //   navigator.clipboard.writeText(JSON.stringify(copiedData));
        //   message.success('已复制选中单元格数据到剪贴板');
        // } else {
        //   message.warning('没有选中任何单元格');
        // }
      });

      // 拖拽列宽结束
      tableInstance.current.on(RESIZE_COLUMN_END, async args => {
        if (args.col === 0) return; // 不处理行头的列宽调整
        const width = args.colWidths[args.col];
        const field = String(columnsRef.current[args.col - 1]?.field);
        await api?.updateField(selectedCollectionRef.current, field, {
          meta: {
            width: width
          }
        });
      });

      setTableReady(true);
    }

    return () => {
      tableInstance.current?.release();
      tableInstance.current = null;
      setTableReady(false);
    };
  }, [api]);

  const refreshTimeoutRef = useRef<any>();
  // 在组件渲染部分，修改传递给 ViewTabs 的数据获取方式
  useEffect(() => {
    // 清除之前的定时器
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // 只有在必要时才刷新视图
    if (selectedCollection && api && !loading) {
      console.log('useEffect 触发视图刷新:', selectedCollection);

      refreshTimeoutRef.current = setTimeout(() => {
        // 检查是否已经有该 collection 的视图数据
        if (!collectionViews[selectedCollection]) {
          console.log('刷新视图数据 for:', selectedCollection);
          refreshViewsFromBackend();
        }
      }, 300); // 延迟执行，避免与其他状态更新冲突
    }

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [selectedCollection, api]);


  return (
    <div>
      <style>{`
        .my-sider { height: 100%; background-color: #F9F9FB;box-shadow: 0 0px 5px rgba(0, 0, 0, 0.2); display: flex; flex-direction: column; border-radius: 30px; }
        .my-sider > .ant-layout-sider-children { margin: 0 10px !important; }
        .my-sider .ellipsis-btn { width: 80%; display: inline-block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .my-sider .sheet-item { display: flex; align-items: center; margin: 5px 0; padding: 10px; border-radius: 15px; cursor: pointer; color: #565D5F; }
        .my-sider .sheet-item:hover { background-color: #F2F3F3; box-shadow: rgba(63, 82, 82, 0.2) 0px 0px 15px 0px; }
        .my-sider .sheet-item .operation-btn { padding: 2px; border-radius: 4px; margin-left: auto; }
        .my-sider .sheet-item .operation-btn:hover { background-color: #E6E8E8; }
        .my-sider .header-icon { border-radius: 4px; padding: 10px; cursor: pointer;color: #565D5F; }
        .my-sider .header-icon:hover { background-color: #E6E8E8; border-radius: 10px; }
        .my-sider .sheet-list { padding:5px 10px }
        .my-sider .dashboard-list {padding:5px 10px}
        .no-arrow-popover .ant-popover-arrow { display: none; }
        .header-icon { cursor: pointer; padding: 5px; }
      `}</style>
      <Layout 
        className='my-layout' 
        style={{
          // height: props.height || '700px', 
          // width:'98vw',
          // boxShadow: 'rgba(63, 82, 82, 0.2) 0px 0px 30px 0px', 
          // borderRadius: '30px', 
          // margin:'20px', 
          // background:'#F9F9FB'
        }}
        >
        <div className='my-main-container'>
          <input
            type="file"
            ref={fileInputRef}
            style={{display: 'none'}}
            accept=".xlsx, .xls, .csv"
            onChange={handleFileUpload}
          />
          <SiderPanel
            height={props.height || '700px'}
            siderCollapsed={siderCollapsed}
            onToggleSider={() => setSiderCollapsed(!siderCollapsed)}
            onSearchSheets={val =>
              setFilterSheetList(
                allSheetList.filter(s =>
                  s.name.toLowerCase().includes(val.toLowerCase())
                )
              )
            }
            filterSheetList={filterSheetList}
            selectedCollection={selectedCollection}
            focusCollection={focusCollection}
            isRenameSheet={isRenameSheet}
            onSelectSheet={onSelectSheet}
            onRenameConfirm={handleRenameConfirm}
            onOperateSheet={onOperateSheet}
            onCreateSheet={onCreateSheet}
            isSheetCardVisible={isSheetCardVisible}
            cardPosition={cardPosition}
            sheetCardRef={sheetCardRef}
            renameInputRef={renameInputRef}
            focusSheetCreateUserId={focusSheet?.createUserId || ''}
            userId={userId}
            focusSheetPermissions={focusSheetPermissions}
            onGetSheetPermissions={() =>
              handleGetSheetPermissions(focusCollection)
            }
            onSetSheetPermissions={onSetSheetPermissions}
            onRenameSheet={() => {
              setRenameSheet(true);
              setSheetCardVisible(false);
            }}
            onCreateCopySheet={onCreateCopySheet}
            onExportSheetAsExcel={onExportAsExcel}
            onExportSheetAsCsv={onExportAsCsv}
            onImportExcelFile={onImportExcelFile}
            onDeleteSheet={onDeleteSheet}
            // --- 仪表盘 Props ---
            onCreateDashboard={onCreateDashboard}
            isDashboardCardVisible={isDashboardCardVisible}
            dashboardCardRef={dashboardCardRef}
            filterDashboardList={filterDashboardList}
            selectedDashboard={selectedDashboard}
            focusDashboard={focusDashboard}
            focusDashboardInfo={focusDashboardInfo}
            onSelectDashboard={onSelectDashboard}
            onOperateDashboard={onOperateDashboard}
            isRenameDashboard={isRenameDashboard}
            onRenameDashboard={() => {
              setRenameDashboard(true);
              setDashboardCardVisible(false);
            }}
            onRenameDashboardConfirm={onRenameDashboardConfirm}
            onDeleteDashboard={onDeleteDashboard}
            onSetDashboardPermissions={onSetDashboardPermissions}
          />
          <Content>
            <Spin spinning={loading} size="large" tip="正在加载中...">
              <div
                style={{
                  // background: 'rgba(255, 255, 255, 0.1)', //#F0F2F5
                  padding: '0 10px',
                  borderRadius: '30px',
                  // boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0px 5px',
                  marginLeft: '0 5px',
                }}
              >
                {/* {activeView === 'sheet' && ( */}
                <div style={{display: activeView === 'sheet' ? 'block' : 'none'}}>
                  <ViewTabs
                    views={currentCollectionState.views}
                    activeViewId={currentCollectionState.activeViewId}
                    onViewChange={handleViewChange}
                    onViewCreate={handleViewCreate}
                    onViewUpdate={handleViewUpdate}
                    onViewDelete={handleViewDelete}
                    onViewRename={handleViewRename}
                    currentFilters={getCurrentViewFilters().filters}
                    currentLogicRelation={getCurrentViewFilters().logicRelation}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        // height: '100%',
                        margin: '10px',
                        // background:'rgba(255, 255, 255, 0.1)',
                        borderRadius: '5px',
                      }}
                    >
                      <SheetHeaderToolbar
                        siderCollapsed={siderCollapsed}
                        onToggleSider={() => setSiderCollapsed(!siderCollapsed)}
                        onAddRow={() => onAddRow('lastRow')}
                        fieldList={fieldList}
                        draggingField={draggingField}
                        handleFieldDragStart={handleFieldDragStart}
                        handleFieldDrop={handleFieldDrop}
                        handleToggleColumnVisibility={(fieldName: string) => {
                          const newFields = fieldList.map(field =>
                            field.field === fieldName
                              ? {...field, visible: !field.visible}
                              : field
                          );
                          setFieldList(newFields);
                        }}
                        currentSheetPermissions={currentSheetPermissions}
                        currentSheetCreateUserId={
                          currentSheet?.createUserId || ''
                        }
                        userId={userId}
                        isLoggedIn={!!api}
                        onAddChartsToDashboard={handleAddChartsToDashboard}
                        currentSheetId={currentSheet?.collection}
                        api={api}
                        props={props}
                        onApplyFilter={handleApplyFilterWithView}
                        activeFilters={getCurrentViewFilters().filters}
                        currentFilterLogicRelation={
                          getCurrentViewFilters().logicRelation
                        }
                        activeViewId={currentCollectionState.activeViewId}
                      />
                      <div
                        ref={containerRef}
                        className="vtable-container-auto-resize" // 添加CSS类
                        style={{
                          height: `calc(${props.height} - 125px)`,
                          minHeight: '200px',
                          width: '100%',
                          padding: '0',
                          marginTop: '10px'
                        }}
                      />
                      <FieldEditorCard
                        key={`${operateType}-${editField}`}
                        visible={isCardVisible}
                        position={cardPosition}
                        operateType={operateType}
                        initialValues={{
                          fieldName: editField,
                          fieldType: selectedFieldType
                        }}
                        selectedFieldType={selectedFieldType}
                        onConfirm={onConfirmField}
                        onCancel={() => setCardVisible(false)}
                        onFieldTypeChange={setSelectedFieldType}
                      />
                    </div>
                  </ViewTabs>
                </div>
                {/* 仪表盘 */}
                <div
                  style={{
                    display: activeView === 'dashboard' ? 'block' : 'none',
                    height: '100%'
                  }}
                >
                  {/* <DashboardHeaderToolbar
                    siderCollapsed={siderCollapsed}
                    onToggleSider={() => setSiderCollapsed(!siderCollapsed)}
                    userId={userId}
                    isLoggedIn={!!api}
                    onAddChart={() => {
                      setChartModalVisible(true);
                    }}
                    dashboard={
                      allDashboardList.find(d => d.id === selectedDashboard) ||
                      null
                    }
                  /> */}
                  {/* <DashboardView
                    loading={loading}
                    dashboard={
                      allDashboardList.find(d => d.id === selectedDashboard) ||
                      null
                    }
                    dashboardId={selectedDashboard}
                    height={props.height || '700px'}
                    api={api}
                    allSheetList={allSheetList}
                    isChartModalVisible={isChartModalVisible}
                    onChartModalVisible={setChartModalVisible}
                    userId={userId}
                  /> */}

                  <DashboardView
                    loading={loading}
                    height={props.height || '700px'}
                    dashboard={
                      allDashboardList.find(d => d.id === selectedDashboard) ||
                      null
                    }
                    parentDashboardId={selectedDashboard}
                    // api={api}
                    parentAllSheetList={allSheetList}
                    siderCollapsed={siderCollapsed}
                    isShowPermissionTag={true}
                    onToggleSider={() => setSiderCollapsed(!siderCollapsed)}
                    // isChartModalVisible={isChartModalVisible}
                    // onChartModalVisible={setChartModalVisible}
                    // userId={userId}
                    {...props} // 传递所有 RendererProps 属性
                  />
                </div>
              </div>
            </Spin>
          </Content>
        </div>
      </Layout>
      <ImportConfigModal
        visible={isImportModalVisible}
        onOk={onConfirmImport}
        onCancel={() => setImportModalVisible(false)}
        headers={importHeaders}
        mappings={columnTypeMappings}
        onTypeChange={(header, type) =>
          setColumnTypeMappings(prev => ({...prev, [header]: type}))
        }
      />
    </div>
  );
};

@Renderer({
  test: /\bagimx-grid$/,
  name: 'agimx-grid'
})
export default class GridPlugin extends React.Component<GridPluginProps> {
  render() {
    return <AgimxGrid {...this.props} />;
  }
}
